import * as React from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  UserCircle,
  ShoppingCart,
  CreditCard,
  CheckCircle,
  FileText,
  AlertTriangle,
  Clock,
  Loader2,
  MoreVertical,
  RefreshCw,
  Mail,
  ExternalLink,
  Download,
  Eye,
  Trash2,
  Pause,
  Play,
  UserX,
} from "lucide-react";

import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import {
  studentApi,
  documentsApi,
  apiRequest,
  getStudentCheckoutTracking,
  fetchAdminSignature,
} from "@/lib/api";
import { enrollmentsApi } from "@/lib/api/enrollments";
import { DocumentList } from "@/components/documents/DocumentList";
import { DocumentViewer } from "@/components/documents/DocumentViewer";
import { logger } from "@/utils/logger";
import config from "@/config";
import {
  formatAdminBusinessEt,
  formatEnrollmentStartDate,
} from "@/lib/admin-datetime";
import type { Document as ApiDocument } from "@/types/document";

type DocumentSummary = {
  total: number;
  uploaded: number;
  required: number;
  optional: number;
  pending: number;
  approved: number;
  rejected: number;
  missingRequired: number;
};

type MissingRequiredDocument = {
  documentType: string;
  label: string;
};

type OverviewResponse = Awaited<
  ReturnType<typeof studentApi.getStudentOverview>
>["data"];

type ComplianceStatus = {
  student?: { firstName?: string; lastName?: string; email?: string } | null;
  totalAgreements?: number;
  agreements?: Array<{
    signatureId?: string;
    orderId?: string;
    program?: string;
    type?: string;
    hasSignature?: boolean;
    signatureType?: string;
    termsVersion?: string;
    agreedAt?: string;
    consentDetails?: {
      dataProcessing?: boolean;
      marketing?: boolean;
      thirdPartySharing?: boolean;
    };
  }>;
  latestAgreement?: {
    signatureId?: string;
    program?: string;
    orderId?: string;
    agreedAt?: string;
    termsVersion?: string;
    hasSignature?: boolean;
  } | null;
  hasActiveSignature?: boolean;
};

type PaymentEntry = {
  _id: string;
  amount: number;
  status: string;
  type: string;
  timestamp: string;
  transactionId?: string;
  programName?: string | null;
  planName?: string | null;
  orderId?: string;
  paymentNumber?: number | null;
  totalPayments?: number | null;
  paymentType?: string | null;
  chargableDueDate?: string | null;
  metadata?: {
    kind?: string;
    paymentType?: string;
    paymentNumber?: number;
    chargableDueDate?: string;
    dueDate?: string;
    reason?: string;
    description?: string;
    manualSource?: string;
    manualReason?: string;
    installmentCountExcluded?: boolean;
    manualReversalReason?: string;
  };
  paymentMethod?: {
    type?: string;
    lastFour?: string;
    cardType?: string;
  };
  order?: { _id: string; program?: string };
  subscription?: {
    squareSubscriptionId?: string;
    status?: string;
    numberOfPayments?: number;
    completedPayments?: number;
    enrollmentDate?: string;
    createdAt?: string;
    cadence?: string;
  };
  failureReason?: string;
  subscriptionId?: string;
  squareSubscriptionId?: string;
};

type UpcomingPayment = {
  _id?: string;
  date?: string;
  scheduledDate?: string;
  amount: number;
  paymentNumber?: number;
  totalPayments?: number;
  isNextPayment?: boolean;
  isOverdue?: boolean;
  isFuture?: boolean;
  daysUntilPayment?: number;
  formattedDate?: string;
  status?: string;
  cadence?: string | null;
  subscriptionId?: string;
  program?: string | null;
  programName?: string | null;
  lateFeeApplied?: boolean;
  lateFeeAmount?: number;
  lateFeeStatus?: "APPLIED" | "PENDING" | "NOT_APPLIED" | "IN_GRACE";
  lateFeeRecordId?: string | null;
  amountWithLateFee?: number;
  inLateFeeGracePeriod?: boolean;
  lateFeeGraceDaysRemaining?: number;
};

type UpcomingPaymentsPayload = {
  total?: number;
  totalAmount?: number;
  nextPayment?: UpcomingPayment | null;
  allPayments?: UpcomingPayment[];
};

function parseUpcomingPaymentsResponse(data: {
  upcomingPayments?: UpcomingPaymentsPayload;
  upcoming?: { payments?: UpcomingPayment[] };
}): {
  rows: UpcomingPayment[];
  summary: UpcomingPaymentsPayload | null;
} {
  if (data.upcomingPayments?.allPayments) {
    return { rows: data.upcomingPayments.allPayments, summary: data.upcomingPayments };
  }
  const legacy = data.upcoming?.payments ?? [];
  if (legacy.length > 0) {
    return {
      rows: legacy,
      summary: {
        total: legacy.length,
        totalAmount: legacy.reduce((s, p) => s + (p.amount || 0), 0),
        allPayments: legacy,
        nextPayment: legacy.find((p) => p.isNextPayment) ?? legacy[0],
      },
    };
  }
  return { rows: [], summary: null };
}

function upcomingPaymentDate(p: UpcomingPayment): string | null {
  return p.date ?? p.scheduledDate ?? null;
}

function upcomingStatusLabel(p: UpcomingPayment): string {
  if (p.isOverdue || p.status === "OVERDUE") return "Overdue";
  if (p.isNextPayment || p.status === "NEXT") return "Next";
  if (p.isFuture === false) return "Past";
  return "Scheduled";
}

function upcomingStatusTone(
  p: UpcomingPayment
): "success" | "warning" | "danger" | "info" | "muted" {
  if (p.isOverdue || p.status === "OVERDUE") return "danger";
  if (p.isNextPayment || p.status === "NEXT") return "info";
  if (p.isFuture === false) return "muted";
  return "warning";
}

function canDownloadChargebackEvidence(p: PaymentEntry): boolean {
  return (
    p.status?.toUpperCase() === "COMPLETED" &&
    p.type?.toUpperCase() === "PAYMENT" &&
    isTuitionPayment(p)
  );
}

const NON_TUITION_KINDS = new Set([
  "BOOKSTORE_ORDER",
  "TRANSCRIPT_FEE",
  "NAME_CHANGE_FEE",
  "COURSE_KEY_EXTENSION",
  "COURSE_KEY_REACTIVATION",
  "DELAY_FEE",
]);

function isTuitionPayment(p: PaymentEntry): boolean {
  const kind = p.metadata?.kind;
  if (kind && NON_TUITION_KINDS.has(kind)) return false;
  if (p.type?.toUpperCase() === "DELAY_FEE") return false;
  return true;
}

function resolveChargableDueDate(p: PaymentEntry): string | null {
  return (
    p.chargableDueDate ||
    p.metadata?.chargableDueDate ||
    p.metadata?.dueDate ||
    null
  );
}

type UnifiedPaymentRow =
  | {
      kind: "history";
      id: string;
      sortAt: number;
      dateIso: string;
      chargableDueDateIso: string | null;
      installmentLabel: string | null;
      detailLabel: string;
      amount: number;
      statusLabel: string;
      statusTone: "success" | "warning" | "danger" | "info" | "muted";
      highlight: boolean;
      history: PaymentEntry;
      lateFeeApplied?: boolean;
      lateFeeAmount?: number;
      lateFeeStatus?: "APPLIED" | "PENDING" | "NOT_APPLIED" | "IN_GRACE";
      amountWithLateFee?: number;
      inLateFeeGracePeriod?: boolean;
      lateFeeGraceDaysRemaining?: number;
    }
  | {
      kind: "upcoming";
      id: string;
      sortAt: number;
      dateIso: string | null;
      chargableDueDateIso: string | null;
      installmentLabel: string | null;
      detailLabel: string;
      amount: number;
      statusLabel: string;
      statusTone: "success" | "warning" | "danger" | "info" | "muted";
      highlight: boolean;
      upcoming: UpcomingPayment;
      lateFeeApplied?: boolean;
      lateFeeAmount?: number;
      lateFeeStatus?: "APPLIED" | "PENDING" | "NOT_APPLIED" | "IN_GRACE";
      amountWithLateFee?: number;
      inLateFeeGracePeriod?: boolean;
      lateFeeGraceDaysRemaining?: number;
    };

function historyInstallmentNumber(p: PaymentEntry): number | null {
  if (p.paymentNumber != null && p.paymentNumber > 0) return p.paymentNumber;
  if (typeof p.metadata?.paymentNumber === "number" && p.metadata.paymentNumber > 0) {
    return p.metadata.paymentNumber;
  }
  return null;
}

function isReversedInstallment(p: PaymentEntry): boolean {
  return p.metadata?.installmentCountExcluded === true;
}

function manualPaidLabel(reason: string | undefined): string | null {
  if (reason === "CASH") return "Cash";
  if (reason === "CHECK_OR_MONEY_ORDER") return "Check";
  if (reason === "OTHER_TRANSFER") return "Transfer";
  return null;
}

function historyStatusLabel(p: PaymentEntry): string {
  if (isReversedInstallment(p)) {
    if (p.metadata?.manualReversalReason === "CHARGEBACK") return "Chargeback";
    if (p.metadata?.manualReversalReason === "OUTSIDE_REFUND") {
      return "Refunded outside Square";
    }
    return "Unpaid";
  }
  const status = p.status?.toUpperCase() ?? "";
  const type = p.type?.toUpperCase() ?? "";
  if (status === "COMPLETED" && type === "PAYMENT") return "Paid";
  if (status === "FAILED") return "Declined";
  if (status === "REFUNDED") return "Refunded";
  if (status === "PENDING") return "Pending";
  return p.status || "—";
}

function historyStatusTone(p: PaymentEntry): UnifiedPaymentRow["statusTone"] {
  const label = historyStatusLabel(p);
  if (label === "Paid") return "success";
  if (
    label === "Declined" ||
    label === "Refunded" ||
    label === "Chargeback" ||
    label === "Refunded outside Square" ||
    label === "Unpaid"
  ) {
    return "danger";
  }
  if (label === "Pending") return "warning";
  return tonedPaymentStatus(p.status);
}

/** One latest paid tuition row per subscription. Reversed rows are not paid. */
function latestPaidPaymentIds(history: PaymentEntry[]): Set<string> {
  const bySubscription = new Map<string, PaymentEntry[]>();
  for (const payment of history) {
    if (!isTuitionPayment(payment)) continue;
    if (payment.type?.toUpperCase() !== "PAYMENT") continue;
    if (payment.status?.toUpperCase() !== "COMPLETED") continue;
    if (isReversedInstallment(payment)) continue;
    const key = payment.subscriptionId || payment.squareSubscriptionId;
    if (!key) continue;
    const list = bySubscription.get(key) ?? [];
    list.push(payment);
    bySubscription.set(key, list);
  }

  const ids = new Set<string>();
  for (const list of bySubscription.values()) {
    const numbered = list
      .map((payment) => ({ payment, number: historyInstallmentNumber(payment) }))
      .filter((row): row is { payment: PaymentEntry; number: number } => row.number != null);
    if (numbered.length > 0) {
      const max = Math.max(...numbered.map((row) => row.number));
      const winner = numbered.find((row) => row.number === max);
      if (winner) ids.add(winner.payment._id);
      continue;
    }
    const sorted = [...list].sort(
      (a, b) => parseSortTimestamp(b.timestamp) - parseSortTimestamp(a.timestamp)
    );
    if (sorted[0]) ids.add(sorted[0]._id);
  }
  return ids;
}

function parseSortTimestamp(value: string | null | undefined): number {
  if (!value) return Number.MAX_SAFE_INTEGER;
  const t = new Date(value).getTime();
  return Number.isNaN(t) ? Number.MAX_SAFE_INTEGER : t;
}

function formatInstallmentLabel(
  paymentNumber: number,
  totalPayments: number
): string {
  return `${paymentNumber} / ${totalPayments}`;
}

function resolveInstallmentTotal(
  history: PaymentEntry[],
  scheduled: UpcomingPayment[]
): number | null {
  for (const u of scheduled) {
    if (u.totalPayments != null && u.totalPayments > 0) return u.totalPayments;
  }
  for (const p of history) {
    if (p.totalPayments != null && p.totalPayments > 0) return p.totalPayments;
    const subTotal = p.subscription?.numberOfPayments;
    if (subTotal != null && subTotal > 0) return subTotal;
  }
  return null;
}

/** Map completed tuition payment IDs → e.g. "1 / 16" */
function buildHistoryInstallmentLabels(
  history: PaymentEntry[],
  totalPayments: number | null
): Map<string, string> {
  const labels = new Map<string, string>();
  if (!totalPayments) return labels;

  const paidTuition = history
    .filter(
      (p) =>
        isTuitionPayment(p) &&
        p.type?.toUpperCase() === "PAYMENT" &&
        p.status?.toUpperCase() === "COMPLETED"
    )
    .sort(
      (a, b) => parseSortTimestamp(a.timestamp) - parseSortTimestamp(b.timestamp)
    );

  paidTuition.forEach((p, idx) => {
    const fromMeta =
      typeof p.metadata?.paymentNumber === "number" && p.metadata.paymentNumber > 0
        ? p.metadata.paymentNumber
        : null;
    const num =
      p.paymentNumber != null && p.paymentNumber > 0
        ? p.paymentNumber
        : fromMeta ?? idx + 1;
    const total =
      p.totalPayments != null && p.totalPayments > 0
        ? p.totalPayments
        : totalPayments;
    labels.set(p._id, formatInstallmentLabel(num, total));
  });

  return labels;
}

function isStaleCheckoutPlaceholder(
  payment: PaymentEntry,
  history: PaymentEntry[],
  scheduled: UpcomingPayment[] = []
): boolean {
  if (payment.status?.toUpperCase() !== "PENDING") return false;
  if (payment.type?.toUpperCase() !== "PAYMENT") return false;

  const tx = payment.transactionId ?? "";
  const squareSubId =
    payment.subscription?.squareSubscriptionId ?? payment.squareSubscriptionId;
  const isPlaceholder =
    tx.startsWith("checkout_pending_") ||
    (squareSubId != null && tx === squareSubId);
  if (!isPlaceholder) return false;

  // Replaced by a real completed charge for the same subscription/order
  const coveredByCompleted = history.some((other) => {
    if (other._id === payment._id) return false;
    if (other.status?.toUpperCase() !== "COMPLETED") return false;
    if (other.type?.toUpperCase() !== "PAYMENT") return false;
    if (!isTuitionPayment(other)) return false;
    if (payment.subscriptionId && other.subscriptionId === payment.subscriptionId) {
      return true;
    }
    if (
      squareSubId &&
      other.subscription?.squareSubscriptionId === squareSubId
    ) {
      return true;
    }
    if (payment.orderId && other.orderId === payment.orderId) {
      return true;
    }
    return false;
  });
  if (coveredByCompleted) return true;

  // Same unpaid installment already appears as upcoming/overdue on the schedule
  // (checkout left a pending placeholder; schedule row is the source of truth).
  if (
    payment.subscriptionId &&
    scheduled.some((u) => u.subscriptionId === payment.subscriptionId)
  ) {
    return true;
  }

  return false;
}

function historyDetailLabel(p: PaymentEntry): string {
  const manualLabel = manualPaidLabel(p.metadata?.manualReason);
  if (
    manualLabel &&
    (p.metadata?.manualSource === "MANUAL_INSTALLMENT" ||
      String(p.metadata?.paymentType ?? "").toUpperCase() === "MANUAL_INSTALLMENT")
  ) {
    return manualLabel.toUpperCase();
  }
  const metaType = p.metadata?.paymentType || p.paymentType;
  if (metaType && String(metaType).toUpperCase() === "ADVANCE") return "ADVANCE";
  if (metaType && String(metaType).toUpperCase() === "INSTALLMENT_ADVANCE") {
    return "ADVANCE";
  }
  if (p.metadata?.kind === "BOOKSTORE_ORDER") return "BOOKSTORE";
  return p.type || "—";
}

function buildUnifiedPaymentRows(
  history: PaymentEntry[],
  scheduled: UpcomingPayment[]
): UnifiedPaymentRow[] {
  const visibleHistory = history.filter(
    (p) =>
      !isStaleCheckoutPlaceholder(p, history, scheduled) && isTuitionPayment(p)
  );
  const rows: UnifiedPaymentRow[] = [];
  const installmentTotal = resolveInstallmentTotal(visibleHistory, scheduled);
  const historyInstallmentLabels = buildHistoryInstallmentLabels(
    visibleHistory,
    installmentTotal
  );

  for (const p of visibleHistory) {
    rows.push({
      kind: "history",
      id: `hist-${p._id}`,
      sortAt: parseSortTimestamp(p.timestamp),
      dateIso: p.timestamp,
      chargableDueDateIso: resolveChargableDueDate(p),
      installmentLabel: historyInstallmentLabels.get(p._id) ?? null,
      detailLabel: historyDetailLabel(p),
      amount: p.amount,
      statusLabel: historyStatusLabel(p),
      statusTone: historyStatusTone(p),
      highlight: false,
      history: p,
    });
  }

  scheduled.forEach((u, idx) => {
    const dateIso = upcomingPaymentDate(u);
    rows.push({
      kind: "upcoming",
      id: `up-${u._id ?? idx}`,
      sortAt: parseSortTimestamp(dateIso),
      dateIso,
      chargableDueDateIso: dateIso,
      installmentLabel:
        u.paymentNumber != null &&
        u.totalPayments != null &&
        u.paymentNumber > 0 &&
        u.totalPayments > 0
          ? formatInstallmentLabel(u.paymentNumber, u.totalPayments)
          : null,
      detailLabel: u.cadence || "Scheduled",
      amount: u.amount,
      statusLabel: upcomingStatusLabel(u),
      statusTone: upcomingStatusTone(u),
      highlight: Boolean(u.isNextPayment),
      upcoming: u,
      lateFeeApplied: Boolean(u.lateFeeApplied),
      lateFeeAmount: u.lateFeeAmount ?? 0,
      lateFeeStatus: u.lateFeeStatus ?? "NOT_APPLIED",
      amountWithLateFee: u.amountWithLateFee ?? u.amount,
      inLateFeeGracePeriod: Boolean(u.inLateFeeGracePeriod),
      lateFeeGraceDaysRemaining: u.lateFeeGraceDaysRemaining ?? 0,
    });
  });

  return rows.sort((a, b) => a.sortAt - b.sortAt);
}

type DelayFeesData = {
  totalDelayFees?: number;
  delayFeesApplied?: number;
  pendingDelayFees?: number;
  lastDelayFeeDate?: string;
  delayFeeRecords?: Array<{
    _id?: string;
    amount: number;
    timestamp: string;
    status: string;
    metadata?: {
      reason?: string;
      description?: string;
      waiverReason?: string;
      waived?: boolean;
      waivedAt?: string;
    };
  }>;
};

type CheckoutEvent = {
  _id: string;
  timestamp: string;
  eventType: string;
  status: string;
  timing?: string;
  amount?: number;
  order?: { _id: string; program: string };
  subscription?: { squareSubscriptionId: string };
};

interface UserDetailsSheetProps {
  open: boolean;
  userId: string | null;
  /** Display name used in the loading header before the overview lands. */
  fallbackName?: string;
  /** Optional tab to open when the sheet mounts for a user. */
  initialTab?: string;
  onOpenChange: (open: boolean) => void;
  onEdit: () => void;
  onDelete: () => void;
  onResendCredentials: () => void;
  onImpersonate: () => void;
  /** Permissions that gate kebab actions. */
  canImpersonate: boolean;
  /** Whether the resend credentials network call is currently in-flight. */
  resendingCredentials: boolean;
  /** Whether the impersonation call is currently in-flight. */
  impersonating: boolean;
}

// ─── Formatting helpers ─────────────────────────────────────────────────────
function formatCurrency(amount: number | null | undefined): string {
  if (amount === null || amount === undefined || Number.isNaN(amount)) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount);
}

function formatDate(value: string | Date | null | undefined): string {
  if (!value) return "—";
  const iso = value instanceof Date ? value.toISOString() : value;
  try {
    return formatAdminBusinessEt(iso);
  } catch {
    return "—";
  }
}

/** Age in full years from DOB; returns null if missing/invalid. */
function ageFromDateOfBirth(
  value: string | Date | null | undefined
): number | null {
  if (!value) return null;
  const dob = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(dob.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const monthDiff = today.getMonth() - dob.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
    age -= 1;
  }
  if (age < 0 || age > 150) return null;
  return age;
}

const SUCCESSFUL_ENROLLMENT_STATUSES = new Set([
  "PAYMENT_COMPLETED",
  "ENROLLED",
  "COMPLETED",
]);

function formatBatchSchedule(
  batch?: string | null,
  batchTime?: string | null
): string | null {
  const parts = [batch?.trim(), batchTime?.trim()].filter(
    (p): p is string => Boolean(p)
  );
  return parts.length > 0 ? parts.join(" · ") : null;
}

function formatPaymentPlanLabel(paymentType?: string | null): string | null {
  if (!paymentType) return null;
  const u = paymentType.toUpperCase();
  if (u === "SUBSCRIPTION") return "Installment plan";
  if (u === "ONE_TIME") return "One-time payment";
  return paymentType;
}

function formatPhoneNumber(phone: string | null | undefined): string {
  if (!phone) return "—";
  const cleaned = String(phone).replace(/\D/g, "");
  const match = cleaned.match(/^(\d{1})(\d{3})(\d{3})(\d{4})$/);
  if (match) return `(${match[2]}) ${match[3]}-${match[4]}`;
  const match10 = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (match10) return `(${match10[1]}) ${match10[2]}-${match10[3]}`;
  return phone;
}

// ─── Small presentational helpers ───────────────────────────────────────────
function InfoRow({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1 rounded-md border border-border/60 bg-muted/30 p-3">
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <p className="text-sm font-medium text-foreground break-words">
        {value || "—"}
      </p>
    </div>
  );
}

function StatusBadge({
  tone,
  children,
}: {
  tone: "success" | "warning" | "danger" | "info" | "muted";
  children: React.ReactNode;
}) {
  const toneMap: Record<typeof tone, string> = {
    success: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30",
    warning: "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30",
    danger: "bg-destructive/15 text-destructive border-destructive/30",
    info: "bg-sky-500/15 text-sky-700 dark:text-sky-300 border-sky-500/30",
    muted: "bg-muted text-muted-foreground border-border",
  };
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${toneMap[tone]}`}
    >
      {children}
    </span>
  );
}

function tonedPaymentStatus(status: string | null | undefined) {
  if (!status) return "muted" as const;
  const u = status.toUpperCase();
  if (u === "COMPLETED" || u === "PAID" || u === "PAYMENT_COMPLETED") return "success" as const;
  if (u === "WAIVED") return "info" as const;
  if (u === "FAILED" || u === "CANCELLED" || u === "REFUNDED") return "danger" as const;
  if (
    u === "PENDING" ||
    u === "PENDING_PAYMENT" ||
    u === "PENDING_MANUAL_CHARGE"
  ) {
    return "warning" as const;
  }
  if (u === "ENROLLED" || u === "ACTIVE") return "info" as const;
  return "muted" as const;
}

function canWaiveDelayFee(status: string | null | undefined): boolean {
  const u = (status || "").toUpperCase();
  return u === "PENDING" || u === "PENDING_MANUAL_CHARGE" || u === "FAILED";
}

/** Overdue schedule row with applied or expected late fee (past grace). */
function canWaiveScheduleLateFee(row: UnifiedPaymentRow): boolean {
  if (row.kind !== "upcoming") return false;
  const overdue =
    row.upcoming.isOverdue ||
    row.upcoming.status === "OVERDUE" ||
    row.statusLabel === "Overdue";
  if (!overdue) return false;
  if (
    row.inLateFeeGracePeriod ||
    row.lateFeeStatus === "IN_GRACE" ||
    row.upcoming.inLateFeeGracePeriod ||
    row.upcoming.lateFeeStatus === "IN_GRACE"
  ) {
    return false;
  }
  const amount = row.lateFeeAmount ?? row.upcoming.lateFeeAmount ?? 0;
  return amount > 0;
}

type WaiveFeeTarget =
  | { mode: "record"; _id: string; amount: number }
  | {
      mode: "installment";
      subscriptionId: string;
      paymentNumber?: number;
      dueDate?: string;
      amount: number;
      lateFeeRecordId?: string | null;
    };

type ManualStatusTarget = {
  action: "MARK_PAID" | "MARK_UNPAID";
  subscriptionId: string;
  paymentNumber: number;
  paymentId?: string;
  amount: number;
  installmentLabel: string | null;
};

const MANUAL_PAID_REASON_OPTIONS = [
  { value: "CASH", label: "Cash at school" },
  { value: "CHECK_OR_MONEY_ORDER", label: "Check or money order" },
  { value: "OTHER_TRANSFER", label: "Zelle, wire, or other transfer" },
] as const;

const MANUAL_UNPAID_REASON_OPTIONS = [
  { value: "CHARGEBACK", label: "Bank chargeback" },
  { value: "OUTSIDE_REFUND", label: "Refund given outside Square" },
] as const;

type PaymentAuthStatus = "not_required" | "pending" | "completed";

type EnrollmentPaymentAuthorization = OverviewResponse["enrollments"][number]["paymentAuthorization"];

function paymentAuthStatusBadge(status: PaymentAuthStatus) {
  if (status === "completed") {
    return <StatusBadge tone="success">Consent on file</StatusBadge>;
  }
  if (status === "pending") {
    return <StatusBadge tone="warning">Consent pending</StatusBadge>;
  }
  return <StatusBadge tone="muted">Not required</StatusBadge>;
}

function paymentAuthEnrollmentDetails(auth: EnrollmentPaymentAuthorization) {
  if (auth.status !== "completed") return null;
  const parts: string[] = [];
  if (auth.cardholderType === "self") parts.push("Self cardholder");
  else if (auth.cardholderType === "third-party") parts.push("Third-party cardholder");
  if (auth.signedAt) {
    parts.push(`Signed ${formatAdminBusinessEt(auth.signedAt)}`);
  }
  if (auth.context === "card-changed") parts.push("Card changed");
  if (parts.length === 0) return null;
  return (
    <p className="mt-1 text-xs text-muted-foreground">{parts.join(" · ")}</p>
  );
}

function paymentAuthDownloadDisabledReason(
  authStatus: PaymentAuthStatus | null | undefined
): string | null {
  if (authStatus === "completed") return null;
  if (authStatus === "pending") {
    return "Student has not submitted payment authorization yet.";
  }
  if (authStatus === "not_required") {
    return "Payment authorization is not required for this enrollment.";
  }
  return "This payment is not the enrollment authorization trigger payment.";
}

function PaymentAuthMenuItem({
  disabled,
  disabledReason,
  busy,
  onSelect,
  children,
}: {
  disabled: boolean;
  disabledReason: string | null;
  busy: boolean;
  onSelect: () => void;
  children: React.ReactNode;
}) {
  const item = (
    <DropdownMenuItem
      disabled={disabled || busy}
      onClick={(e) => {
        e.preventDefault();
        if (!disabled && !busy) onSelect();
      }}
    >
      {children}
    </DropdownMenuItem>
  );

  if (!disabledReason) return item;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="block w-full">{item}</span>
      </TooltipTrigger>
      <TooltipContent side="left" className="max-w-xs">
        {disabledReason}
      </TooltipContent>
    </Tooltip>
  );
}

// ─── Compliance API (matches existing /api/admin/auth/signature/status) ────
async function fetchComplianceStatus(studentId: string): Promise<ComplianceStatus> {
  const res = await apiRequest<{ data: ComplianceStatus }>(
    `/api/admin/auth/signature/status?studentId=${studentId}`,
    { method: "GET" }
  );
  return res?.data ?? {};
}

async function fetchUserDelayFees(studentId: string): Promise<DelayFeesData | null> {
  try {
    const res = await apiRequest<{ data: DelayFeesData }>(
      `/api/admin/delay-fees/student/${studentId}`,
      { method: "GET" }
    );
    return res?.data ?? null;
  } catch (err) {
    logger.error(
      err instanceof Error ? err : new Error("Failed to fetch delay fees"),
      { studentId }
    );
    return null;
  }
}

// ─── Component ──────────────────────────────────────────────────────────────
export function UserDetailsSheet({
  open,
  userId,
  fallbackName,
  initialTab,
  onOpenChange,
  onEdit,
  onDelete,
  onResendCredentials,
  onImpersonate,
  canImpersonate,
  resendingCredentials,
  impersonating,
}: UserDetailsSheetProps) {
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = React.useState(initialTab || "profile");

  // Overview (cheap, fired once per user)
  const [overview, setOverview] = React.useState<OverviewResponse | null>(null);
  const [overviewLoading, setOverviewLoading] = React.useState(false);

  // Lazy per-tab caches — keyed implicitly by userId, cleared when userId changes.
  const [payments, setPayments] = React.useState<PaymentEntry[] | null>(null);
  const [upcoming, setUpcoming] = React.useState<UpcomingPayment[] | null>(null);
  const [upcomingSummary, setUpcomingSummary] =
    React.useState<UpcomingPaymentsPayload | null>(null);
  const [paymentsLoading, setPaymentsLoading] = React.useState(false);
  const [evidenceDownloadingId, setEvidenceDownloadingId] = React.useState<
    string | null
  >(null);
  const [authDownloadingId, setAuthDownloadingId] = React.useState<
    string | null
  >(null);

  const [delayFees, setDelayFees] = React.useState<DelayFeesData | null>(null);
  const [delayFeesLoading, setDelayFeesLoading] = React.useState(false);
  const [waiveFeeTarget, setWaiveFeeTarget] =
    React.useState<WaiveFeeTarget | null>(null);
  const [waiveReason, setWaiveReason] = React.useState("");
  const [waiveSubmitting, setWaiveSubmitting] = React.useState(false);
  const [manualTarget, setManualTarget] =
    React.useState<ManualStatusTarget | null>(null);
  const [manualReason, setManualReason] = React.useState("");
  const [manualNote, setManualNote] = React.useState("");
  const [manualSubmitting, setManualSubmitting] = React.useState(false);

  const [checkoutEvents, setCheckoutEvents] = React.useState<CheckoutEvent[] | null>(null);
  const [checkoutTotal, setCheckoutTotal] = React.useState(0);
  const [checkoutPage, setCheckoutPage] = React.useState(1);
  const [checkoutLoading, setCheckoutLoading] = React.useState(false);

  const [compliance, setCompliance] = React.useState<ComplianceStatus | null>(null);
  const [complianceLoading, setComplianceLoading] = React.useState(false);
  const [signatureModal, setSignatureModal] = React.useState<{
    open: boolean;
    loading: boolean;
    url?: string;
    timestamp?: string;
  }>({ open: false, loading: false });

  const [documents, setDocuments] = React.useState<ApiDocument[] | null>(null);
  const [documentSummary, setDocumentSummary] = React.useState<DocumentSummary | null>(null);
  const [missingRequiredDocs, setMissingRequiredDocs] = React.useState<MissingRequiredDocument[]>([]);
  const [documentsLoading, setDocumentsLoading] = React.useState(false);
  const [docsUpdating, setDocsUpdating] = React.useState<Set<string>>(new Set());
  const [docViewingId, setDocViewingId] = React.useState<string | null>(null);
  const [docViewerOpen, setDocViewerOpen] = React.useState(false);
  const [viewerDoc, setViewerDoc] = React.useState<ApiDocument | null>(null);
  const [viewerUrl, setViewerUrl] = React.useState<string | undefined>(undefined);

  const [confirmDelete, setConfirmDelete] = React.useState(false);
  const [lifecycleAction, setLifecycleAction] = React.useState<
    "pause" | "resume" | "drop" | null
  >(null);
  const [lifecycleEnrollmentId, setLifecycleEnrollmentId] = React.useState<
    string | null
  >(null);
  const [lifecycleReason, setLifecycleReason] = React.useState("");
  const [lifecycleSubmitting, setLifecycleSubmitting] = React.useState(false);

  // Reset caches whenever the user changes or the sheet is reopened for a new user.
  React.useEffect(() => {
    setActiveTab(initialTab || "profile");
    setOverview(null);
    setPayments(null);
    setUpcoming(null);
    setUpcomingSummary(null);
    setDelayFees(null);
    setWaiveFeeTarget(null);
    setWaiveReason("");
    setManualTarget(null);
    setManualReason("");
    setManualNote("");
    setCheckoutEvents(null);
    setCheckoutTotal(0);
    setCheckoutPage(1);
    setCompliance(null);
    setDocuments(null);
    setDocumentSummary(null);
    setMissingRequiredDocs([]);
    setDocViewerOpen(false);
    setViewerDoc(null);
    setViewerUrl(undefined);
    setDocViewingId(null);
    setLifecycleAction(null);
    setLifecycleReason("");
  }, [userId, initialTab]);

  // ─── Overview fetch (on open) ────────────────────────────────────────────
  React.useEffect(() => {
    if (!open || !userId) return;
    let cancelled = false;
    setOverviewLoading(true);
    studentApi
      .getStudentOverview(userId)
      .then((res) => {
        if (cancelled) return;
        setOverview(res.data);
      })
      .catch((err) => {
        if (cancelled) return;
        logger.error(
          err instanceof Error ? err : new Error("Failed to load overview"),
          { userId }
        );
        toast.error(
          err instanceof Error ? err.message : "Failed to load user overview"
        );
        setOverview(null);
      })
      .finally(() => {
        if (!cancelled) setOverviewLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, userId]);

  // ─── Lazy: Payments tab ──────────────────────────────────────────────────
  const loadPayments = React.useCallback(
    async (force = false) => {
      if (!userId) return;
      if (!force && payments) return;
      setPaymentsLoading(true);
      try {
        const res = await apiRequest<{
          success: boolean;
          data: {
            payments: PaymentEntry[];
            upcomingPayments?: UpcomingPaymentsPayload;
            upcoming?: { payments?: UpcomingPayment[] };
          };
        }>(
          `/api/admin/auth/payments?studentId=${userId}&include=upcoming&limit=50`,
          { method: "GET" }
        );
        setPayments(res?.data?.payments ?? []);
        const parsed = parseUpcomingPaymentsResponse(res?.data ?? {});
        setUpcoming(parsed.rows);
        setUpcomingSummary(parsed.summary);
      } catch (err) {
        logger.error(
          err instanceof Error ? err : new Error("Failed to load payments"),
          { userId }
        );
        toast.error("Failed to load payments");
      } finally {
        setPaymentsLoading(false);
      }
    },
    [userId, payments]
  );

  const loadDelayFees = React.useCallback(
    async (force = false) => {
      if (!userId) return;
      if (!force && delayFees) return;
      setDelayFeesLoading(true);
      const data = await fetchUserDelayFees(userId);
      setDelayFees(data);
      setDelayFeesLoading(false);
    },
    [userId, delayFees]
  );

  const handleWaiveDelayFee = React.useCallback(async () => {
    if (!waiveFeeTarget) return;
    const reason = waiveReason.trim();
    if (reason.length < 3) {
      toast.error("Enter a waiver reason (at least 3 characters)");
      return;
    }
    setWaiveSubmitting(true);
    try {
      if (waiveFeeTarget.mode === "record") {
        await apiRequest(`/api/admin/delay-fees/waive/${waiveFeeTarget._id}`, {
          method: "POST",
          body: { reason },
        });
      } else if (waiveFeeTarget.lateFeeRecordId) {
        await apiRequest(
          `/api/admin/delay-fees/waive/${waiveFeeTarget.lateFeeRecordId}`,
          {
            method: "POST",
            body: { reason },
          }
        );
      } else {
        await apiRequest(`/api/admin/delay-fees/waive-installment`, {
          method: "POST",
          body: {
            subscriptionId: waiveFeeTarget.subscriptionId,
            paymentNumber: waiveFeeTarget.paymentNumber,
            dueDate: waiveFeeTarget.dueDate,
            reason,
          },
        });
      }
      toast.success("Late fee waived — student owes installment only");
      setWaiveFeeTarget(null);
      setWaiveReason("");
      await loadDelayFees(true);
      await loadPayments(true);
    } catch (err) {
      logger.error(
        err instanceof Error ? err : new Error("Failed to waive delay fee"),
        { waiveFeeTarget }
      );
      toast.error(
        err instanceof Error ? err.message : "Failed to waive delay fee"
      );
    } finally {
      setWaiveSubmitting(false);
    }
  }, [waiveFeeTarget, waiveReason, loadDelayFees, loadPayments]);

  const handleManualInstallmentStatus = React.useCallback(async () => {
    if (!manualTarget || !manualReason) return;
    setManualSubmitting(true);
    try {
      await apiRequest("/api/admin/auth/payments/manual-status", {
        method: "POST",
        body: {
          subscriptionId: manualTarget.subscriptionId,
          paymentNumber: manualTarget.paymentNumber,
          action: manualTarget.action,
          reason: manualReason,
          note: manualNote.trim() || undefined,
          paymentId: manualTarget.paymentId,
        },
      });
      toast.success(
        manualTarget.action === "MARK_PAID"
          ? "Installment marked paid"
          : "Installment marked unpaid"
      );
      setManualTarget(null);
      setManualReason("");
      setManualNote("");
      await loadPayments(true);
    } catch (err) {
      logger.error(
        err instanceof Error ? err : new Error("Failed to update installment status"),
        { manualTarget }
      );
      toast.error(
        err instanceof Error ? err.message : "Could not update this installment"
      );
    } finally {
      setManualSubmitting(false);
    }
  }, [manualTarget, manualReason, manualNote, loadPayments]);

  const loadCheckoutEvents = React.useCallback(
    async (page = 1) => {
      if (!userId) return;
      setCheckoutLoading(true);
      try {
        const res = await getStudentCheckoutTracking(userId, {
          limit: 10,
          page,
        });
        const normalized = (res.data.events || []).map((event: CheckoutEvent) => ({
          ...event,
          timing: event.timing
            ? event.timing.toLowerCase().replace("_", "-")
            : undefined,
        }));
        setCheckoutEvents(normalized);
        setCheckoutTotal(res.data.pagination?.totalItems || 0);
        setCheckoutPage(page);
      } catch (err) {
        logger.error(
          err instanceof Error ? err : new Error("Failed to load checkout events"),
          { userId, page }
        );
        toast.error("Failed to load checkout activity");
      } finally {
        setCheckoutLoading(false);
      }
    },
    [userId]
  );

  // ─── Lazy: Compliance tab ────────────────────────────────────────────────
  const loadCompliance = React.useCallback(
    async (force = false) => {
      if (!userId) return;
      if (!force && compliance) return;
      setComplianceLoading(true);
      try {
        const data = await fetchComplianceStatus(userId);
        setCompliance(data);
      } catch (err) {
        logger.error(
          err instanceof Error ? err : new Error("Failed to load compliance"),
          { userId }
        );
        toast.error("Failed to load compliance status");
      } finally {
        setComplianceLoading(false);
      }
    },
    [userId, compliance]
  );

  const handleViewSignature = React.useCallback(async (signatureId: string) => {
    setSignatureModal({ open: true, loading: true });
    try {
      const res = await fetchAdminSignature(signatureId);
      const data = res?.data as {
        signature?: { s3Url?: string; timestamp?: string; data?: string };
      };
      const sig = data?.signature;
      setSignatureModal({
        open: true,
        loading: false,
        url: sig?.s3Url || sig?.data,
        timestamp: sig?.timestamp,
      });
    } catch (err) {
      logger.error(
        err instanceof Error ? err : new Error("Failed to fetch signature"),
        { signatureId }
      );
      toast.error("Failed to load signature image");
      setSignatureModal({ open: false, loading: false });
    }
  }, []);

  // ─── Lazy: Documents tab ─────────────────────────────────────────────────
  const loadDocuments = React.useCallback(
    async (force = false) => {
      if (!userId) return;
      if (!force && documents) return;
      setDocumentsLoading(true);
      try {
        const res = await documentsApi.getByStudentId(userId);
        setDocuments(res.data.documents);
        setDocumentSummary(res.data.summary);
        setMissingRequiredDocs(res.data.missingRequired ?? []);
      } catch (err) {
        logger.error(
          err instanceof Error ? err : new Error("Failed to load documents"),
          { userId }
        );
        toast.error("Failed to load documents");
      } finally {
        setDocumentsLoading(false);
      }
    },
    [userId, documents]
  );

  const handleDocApprove = React.useCallback(
    async (
      documentId: string,
      notes?: string,
      target: "document" | "pending_update" = "document",
    ) => {
      setDocsUpdating((prev) => new Set(prev).add(documentId));
      try {
        await documentsApi.approve(documentId, notes, target);
        toast.success(
          target === "pending_update"
            ? "Document update approved"
            : "Document approved",
        );
        await loadDocuments(true);
      } catch (err) {
        logger.error(
          err instanceof Error ? err : new Error("Failed to approve document"),
          { documentId }
        );
        toast.error("Failed to approve document");
      } finally {
        setDocsUpdating((prev) => {
          const next = new Set(prev);
          next.delete(documentId);
          return next;
        });
      }
    },
    [loadDocuments]
  );

  const handleDocReject = React.useCallback(
    async (
      documentId: string,
      reason: string,
      notes?: string,
      target: "document" | "pending_update" = "document",
    ) => {
      setDocsUpdating((prev) => new Set(prev).add(documentId));
      try {
        await documentsApi.reject(documentId, reason, notes, target);
        toast.success(
          target === "pending_update"
            ? "Update rejected; previous approval kept"
            : "Document rejected",
        );
        await loadDocuments(true);
      } catch (err) {
        logger.error(
          err instanceof Error ? err : new Error("Failed to reject document"),
          { documentId }
        );
        toast.error("Failed to reject document");
      } finally {
        setDocsUpdating((prev) => {
          const next = new Set(prev);
          next.delete(documentId);
          return next;
        });
      }
    },
    [loadDocuments]
  );

  const handleDocView = React.useCallback(async (
    doc: ApiDocument,
    opts?: { pending?: boolean },
  ) => {
    setDocViewingId(doc._id);
    try {
      const res = await documentsApi.getById(doc._id);
      const fetchedDoc = res.data?.document;
      const viewUrl = opts?.pending
        ? res.data?.pendingFile?.viewUrl
        : res.data?.file?.viewUrl;
      if (!fetchedDoc) {
        throw new Error("Document not found");
      }
      if (opts?.pending && !viewUrl) {
        throw new Error("No replacement file available to preview");
      }
      setViewerDoc(fetchedDoc);
      setViewerUrl(viewUrl);
      setDocViewerOpen(true);
      if (!viewUrl) {
        toast.message("Document loaded", {
          description: "No preview URL returned for this file.",
        });
      }
    } catch (err) {
      logger.error(
        err instanceof Error ? err : new Error("Failed to load document"),
        { documentId: doc._id }
      );
      toast.error(
        err instanceof Error ? err.message : "Failed to open document"
      );
    } finally {
      setDocViewingId(null);
    }
  }, []);

  // ─── Tab change effects (lazy loading) ───────────────────────────────────
  React.useEffect(() => {
    if (!open || !userId) return;
    if (activeTab === "payments") {
      void loadPayments();
      void loadDelayFees();
    } else if (activeTab === "compliance") {
      void loadCompliance();
    } else if (activeTab === "documents") {
      void loadDocuments();
    }
  }, [open, userId, activeTab, loadPayments, loadDelayFees, loadCompliance, loadDocuments]);

  // ─── Action handlers ─────────────────────────────────────────────────────
  const handleExportPayments = React.useCallback(
    (kind: "payments" | "orders" | "upcoming") => {
      if (!userId) return;
      const downloadUrl = `${config.apiUrl}/api/admin/auth/payments/export?studentId=${userId}&type=${kind}&format=csv`;
      window.open(downloadUrl, "_blank", "noopener,noreferrer");
    },
    [userId]
  );

  const handleDownloadChargebackEvidence = React.useCallback(
    async (paymentId: string) => {
      setEvidenceDownloadingId(paymentId);
      try {
        const base = config.apiUrl.replace(/\/$/, "");
        const response = await fetch(
          `${base}/api/admin/auth/payments/${paymentId}/chargeback-evidence.pdf`,
          { method: "GET", credentials: "include" }
        );
        if (!response.ok) {
          let message = `Download failed (${response.status})`;
          try {
            const json = (await response.json()) as { message?: string };
            if (json.message) message = json.message;
          } catch {
            const text = await response.text().catch(() => "");
            if (text) message = text.slice(0, 200);
          }
          throw new Error(message);
        }
        const blob = await response.blob();
        const disposition = response.headers.get("Content-Disposition");
        let filename = `payment-evidence-${paymentId}.pdf`;
        const match = disposition?.match(/filename="?([^";\n]+)"?/i);
        if (match?.[1]) filename = match[1].trim();
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.download = filename;
        document.body.appendChild(anchor);
        anchor.click();
        URL.revokeObjectURL(url);
        document.body.removeChild(anchor);
        toast.success("Chargeback evidence PDF downloaded");
      } catch (err) {
        toast.error(
          err instanceof Error ? err.message : "Failed to download evidence PDF"
        );
      } finally {
        setEvidenceDownloadingId(null);
      }
    },
    []
  );

  const handleDownloadPaymentAuthorization = React.useCallback(
    async (paymentId: string) => {
      setAuthDownloadingId(paymentId);
      try {
        const base = config.apiUrl.replace(/\/$/, "");
        const response = await fetch(
          `${base}/api/admin/auth/payments/${paymentId}/payment-authorization.pdf`,
          { method: "GET", credentials: "include" }
        );
        if (!response.ok) {
          let message = `Download failed (${response.status})`;
          try {
            const json = (await response.json()) as { message?: string };
            if (json.message) message = json.message;
          } catch {
            const text = await response.text().catch(() => "");
            if (text) message = text.slice(0, 200);
          }
          throw new Error(message);
        }
        const blob = await response.blob();
        const disposition = response.headers.get("Content-Disposition");
        let filename = `payment-authorization-${paymentId}.pdf`;
        const match = disposition?.match(/filename="?([^";\n]+)"?/i);
        if (match?.[1]) filename = match[1].trim();
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.download = filename;
        document.body.appendChild(anchor);
        anchor.click();
        URL.revokeObjectURL(url);
        document.body.removeChild(anchor);
        toast.success("Payment authorization PDF downloaded");
      } catch (err) {
        toast.error(
          err instanceof Error
            ? err.message
            : "Failed to download authorization PDF"
        );
      } finally {
        setAuthDownloadingId(null);
      }
    },
    []
  );

  // ─── Render helpers ──────────────────────────────────────────────────────
  const student = overview?.student;
  const enrollments = overview?.enrollments ?? [];
  const paymentAuthorizationSummary = overview?.paymentAuthorizationSummary;
  const paymentSummary = overview?.paymentSummary;
  const documentsSummary = overview?.documentsSummary;
  const complianceSummary = overview?.complianceSummary;

  const paymentAuthByPaymentId = React.useMemo(() => {
    const map = new Map<string, PaymentAuthStatus>();
    for (const enrollment of enrollments) {
      const auth = enrollment.paymentAuthorization;
      if (auth?.triggerPaymentId) {
        map.set(auth.triggerPaymentId, auth.status);
      }
    }
    return map;
  }, [enrollments]);

  const unifiedPaymentRows = React.useMemo(
    () => buildUnifiedPaymentRows(payments ?? [], upcoming ?? []),
    [payments, upcoming]
  );
  const latestPaidIds = React.useMemo(
    () => latestPaidPaymentIds(payments ?? []),
    [payments]
  );

  const hasPaymentSchedule = unifiedPaymentRows.length > 0;

  /** Primary enrollment for Profile → Program (student fields, then latest successful order). */
  const primaryEnrollment = React.useMemo(() => {
    return (
      enrollments.find(
        (e) => e.orderStatus && SUCCESSFUL_ENROLLMENT_STATUSES.has(e.orderStatus)
      ) ?? enrollments[0] ?? null
    );
  }, [enrollments]);

  const primaryProgramStatus = (
    primaryEnrollment?.programStatus || "ACTIVE"
  ).toUpperCase();

  const programStatusOf = React.useCallback((e: { programStatus?: string | null }) => {
    return (e.programStatus || "ACTIVE").toUpperCase();
  }, []);

  /** Enrollments that can receive the current lifecycle action (program-scoped). */
  const lifecycleCandidates = React.useMemo(() => {
    if (!lifecycleAction) return [];
    return enrollments.filter((e) => {
      const status = programStatusOf(e);
      if (lifecycleAction === "pause") return status === "ACTIVE";
      if (lifecycleAction === "resume") return status === "PAUSED";
      if (lifecycleAction === "drop") return status === "ACTIVE" || status === "PAUSED";
      return false;
    });
  }, [enrollments, lifecycleAction, programStatusOf]);

  const lifecycleTargetEnrollment = React.useMemo(() => {
    if (!lifecycleEnrollmentId) return lifecycleCandidates[0] ?? null;
    return (
      lifecycleCandidates.find((e) => e._id === lifecycleEnrollmentId) ??
      lifecycleCandidates[0] ??
      null
    );
  }, [lifecycleCandidates, lifecycleEnrollmentId]);

  const hasActiveEnrollment = enrollments.some((e) => programStatusOf(e) === "ACTIVE");
  const hasPausedEnrollment = enrollments.some((e) => programStatusOf(e) === "PAUSED");
  const hasDroppableEnrollment = enrollments.some((e) => {
    const s = programStatusOf(e);
    return s === "ACTIVE" || s === "PAUSED";
  });

  const openLifecycleDialog = React.useCallback(
    (action: "pause" | "resume" | "drop") => {
      const candidates = enrollments.filter((e) => {
        const status = programStatusOf(e);
        if (action === "pause") return status === "ACTIVE";
        if (action === "resume") return status === "PAUSED";
        return status === "ACTIVE" || status === "PAUSED";
      });
      setLifecycleReason("");
      setLifecycleEnrollmentId(candidates[0]?._id ?? null);
      setLifecycleAction(action);
    },
    [enrollments, programStatusOf]
  );

  const profileProgram = React.useMemo(() => {
    const primary = primaryEnrollment;

    const programFromOrder = primary
      ? primary.programName || primary.program || null
      : null;

    return {
      orderId: primary?._id ?? null,
      programEnrolled: student?.programEnrolled ?? programFromOrder,
      deliveryMode: student?.deliveryMode ?? primary?.deliveryMode ?? null,
      graduation: student?.graduation ?? null,
      enrollmentDateEt: primary
        ? formatDate(primary.enrollmentDate ?? primary.createdAt)
        : null,
      programStart: primary
        ? formatEnrollmentStartDate(primary.startDate, primary.startMonth)
        : null,
      batchSchedule: primary
        ? formatBatchSchedule(primary.batch, primary.batchTime)
        : null,
      paymentPlan: primary
        ? formatPaymentPlanLabel(primary.paymentType)
        : null,
      enrollmentStatus: primary?.orderStatus ?? null,
      programStatus: primary?.programStatus ?? null,
      tuitionAmount: primary?.paymentAmount ?? null,
      totalEnrollments: enrollments.length,
    };
  }, [student, enrollments, primaryEnrollment]);

  const refreshOverview = React.useCallback(async () => {
    if (!userId) return;
    try {
      const res = await studentApi.getStudentOverview(userId);
      setOverview(res.data);
    } catch (err) {
      logger.error(
        err instanceof Error ? err : new Error("Failed to refresh overview"),
        { userId }
      );
    }
  }, [userId]);

  const submitLifecycleAction = React.useCallback(async () => {
    const orderId = lifecycleTargetEnrollment?._id;
    if (!lifecycleAction || !orderId) return;
    setLifecycleSubmitting(true);
    try {
      const reason = lifecycleReason.trim() || undefined;
      const programLabel =
        lifecycleTargetEnrollment.programName ||
        lifecycleTargetEnrollment.program ||
        "program";
      if (lifecycleAction === "pause") {
        await enrollmentsApi.pause(orderId, { reason });
        toast.success(`Paused: ${programLabel}`);
      } else if (lifecycleAction === "resume") {
        await enrollmentsApi.resume(orderId);
        toast.success(`Resumed: ${programLabel}`);
      } else {
        await enrollmentsApi.drop(orderId, { reason });
        toast.success(`Dropped: ${programLabel}`);
      }
      setLifecycleAction(null);
      setLifecycleEnrollmentId(null);
      setLifecycleReason("");
      await refreshOverview();
      if (activeTab === "payments") {
        void loadPayments(true);
      } else {
        setPayments(null);
        setUpcoming(null);
        setUpcomingSummary(null);
      }
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to update enrollment"
      );
    } finally {
      setLifecycleSubmitting(false);
    }
  }, [
    lifecycleAction,
    lifecycleTargetEnrollment,
    lifecycleReason,
    refreshOverview,
    activeTab,
    loadPayments,
  ]);

  const headerName =
    student?.fullName ||
    [student?.firstName, student?.lastName].filter(Boolean).join(" ") ||
    student?.email ||
    fallbackName ||
    "User";

  // ─── Render ──────────────────────────────────────────────────────────────
  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="w-full sm:max-w-3xl overflow-y-auto border-l border-border p-0">
          {/* Sticky header */}
          <div className="sticky top-0 z-10 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 border-b border-border px-6 py-4">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3 min-w-0">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10">
                  <UserCircle className="h-5 w-5 text-primary" />
                </div>
                <div className="min-w-0">
                  <SheetTitle className="truncate text-lg font-semibold text-foreground">
                    {headerName}
                  </SheetTitle>
                  <SheetDescription className="truncate text-xs text-muted-foreground">
                    {student?.email || "Loading user details…"}
                  </SheetDescription>
                  {student && (
                    <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                      <StatusBadge
                        tone={
                          student.isBlocked
                            ? "danger"
                            : student.isDeleted
                            ? "muted"
                            : "success"
                        }
                      >
                        {student.isBlocked
                          ? "Blocked"
                          : student.isDeleted
                          ? "Deleted"
                          : "Active"}
                      </StatusBadge>
                      {primaryEnrollment && (
                        <StatusBadge
                          tone={
                            primaryProgramStatus === "PAUSED"
                              ? "warning"
                              : primaryProgramStatus === "CANCELLED"
                              ? "danger"
                              : primaryProgramStatus === "COMPLETED"
                              ? "info"
                              : "success"
                          }
                        >
                          Program:{" "}
                          {primaryProgramStatus === "CANCELLED"
                            ? "Dropped"
                            : primaryProgramStatus === "PAUSED"
                            ? "Paused"
                            : primaryProgramStatus === "COMPLETED"
                            ? "Completed"
                            : "Active"}
                        </StatusBadge>
                      )}
                      {student.requiresPasswordReset && (
                        <StatusBadge tone="warning">
                          Password reset required
                        </StatusBadge>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                {userId && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      navigate(`/users/${userId}`);
                    }}
                    title="Open full page"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                )}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" aria-label="More actions">
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuLabel>Actions</DropdownMenuLabel>
                    <DropdownMenuItem onClick={onEdit}>
                      <UserCircle className="mr-2 h-4 w-4" />
                      Edit user
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={onResendCredentials}
                      disabled={resendingCredentials}
                    >
                      {resendingCredentials ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <Mail className="mr-2 h-4 w-4" />
                      )}
                      Resend credentials
                    </DropdownMenuItem>
                    {canImpersonate && (
                      <DropdownMenuItem
                        onClick={onImpersonate}
                        disabled={impersonating}
                      >
                        {impersonating ? (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                          <ExternalLink className="mr-2 h-4 w-4" />
                        )}
                        View as user
                      </DropdownMenuItem>
                    )}
                    {hasActiveEnrollment && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => openLifecycleDialog("pause")}
                          disabled={lifecycleSubmitting}
                        >
                          <Pause className="mr-2 h-4 w-4" />
                          Pause enrollment
                        </DropdownMenuItem>
                      </>
                    )}
                    {hasPausedEnrollment && (
                      <>
                        {!hasActiveEnrollment ? <DropdownMenuSeparator /> : null}
                        <DropdownMenuItem
                          onClick={() => openLifecycleDialog("resume")}
                          disabled={lifecycleSubmitting}
                        >
                          <Play className="mr-2 h-4 w-4" />
                          Resume enrollment
                        </DropdownMenuItem>
                      </>
                    )}
                    {hasDroppableEnrollment && (
                      <DropdownMenuItem
                        onClick={() => openLifecycleDialog("drop")}
                        disabled={lifecycleSubmitting}
                        className="text-destructive focus:text-destructive"
                      >
                        <UserX className="mr-2 h-4 w-4" />
                        Drop enrollment
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => setConfirmDelete(true)}
                      className="text-destructive focus:text-destructive"
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete user
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="px-6 py-5">
            {overviewLoading && !overview ? (
              <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                <Loader2 className="mb-3 h-8 w-8 animate-spin text-primary" />
                <p className="text-sm font-medium text-foreground">
                  Loading user details…
                </p>
              </div>
            ) : !overview ? (
              <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                <AlertTriangle className="mb-3 h-8 w-8 text-destructive" />
                <p className="text-sm font-medium text-foreground">
                  Failed to load user details
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-3"
                  onClick={() => userId && void loadPayments(true)}
                >
                  Retry
                </Button>
              </div>
            ) : (
              <Tabs
                value={activeTab}
                onValueChange={setActiveTab}
                className="space-y-5"
              >
                {(paymentAuthorizationSummary?.pendingCount ?? 0) > 0 && (
                  <Alert className="border-amber-500/40 bg-amber-500/10">
                    <AlertTriangle className="text-amber-700 dark:text-amber-300" />
                    <AlertTitle className="text-amber-900 dark:text-amber-100">
                      Payment authorization pending
                    </AlertTitle>
                    <AlertDescription className="text-amber-800/90 dark:text-amber-200/90">
                      {paymentAuthorizationSummary?.pendingCount === 1
                        ? "1 enrollment is awaiting payment authorization consent from the student."
                        : `${paymentAuthorizationSummary?.pendingCount} enrollments are awaiting payment authorization consent from the student.`}
                    </AlertDescription>
                  </Alert>
                )}

                <TabsList className="grid w-full grid-cols-5 h-auto p-1">
                  <TabsTrigger value="profile" className="flex items-center gap-1.5 py-2 text-xs">
                    <UserCircle className="h-3.5 w-3.5" />
                    <span className="hidden sm:inline">Profile</span>
                  </TabsTrigger>
                  <TabsTrigger value="enrollments" className="flex items-center gap-1.5 py-2 text-xs">
                    <ShoppingCart className="h-3.5 w-3.5" />
                    <span className="hidden sm:inline">Enrollments</span>
                    {enrollments.length > 0 && (
                      <Badge variant="secondary" className="ml-0.5 h-4 px-1 text-[10px]">
                        {enrollments.length}
                      </Badge>
                    )}
                    {(paymentAuthorizationSummary?.pendingCount ?? 0) > 0 && (
                      <Badge
                        variant="outline"
                        className="ml-0.5 h-4 border-amber-500/50 bg-amber-500/15 px-1 text-[10px] text-amber-800 dark:text-amber-200"
                      >
                        {paymentAuthorizationSummary?.pendingCount}
                      </Badge>
                    )}
                  </TabsTrigger>
                  <TabsTrigger value="payments" className="flex items-center gap-1.5 py-2 text-xs">
                    <CreditCard className="h-3.5 w-3.5" />
                    <span className="hidden sm:inline">Payments</span>
                  </TabsTrigger>
                  <TabsTrigger value="compliance" className="flex items-center gap-1.5 py-2 text-xs">
                    <CheckCircle className="h-3.5 w-3.5" />
                    <span className="hidden sm:inline">Compliance</span>
                  </TabsTrigger>
                  <TabsTrigger value="documents" className="flex items-center gap-1.5 py-2 text-xs">
                    <FileText className="h-3.5 w-3.5" />
                    <span className="hidden sm:inline">Documents</span>
                    {documentsSummary && documentsSummary.pendingReview > 0 && (
                      <Badge variant="destructive" className="ml-0.5 h-4 px-1 text-[10px]">
                        {documentsSummary.pendingReview}
                      </Badge>
                    )}
                  </TabsTrigger>
                </TabsList>

                {/* ─── Profile ─────────────────────────────────────────── */}
                <TabsContent value="profile" className="space-y-4">
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base">Identity</CardTitle>
                    </CardHeader>
                    <CardContent className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <InfoRow label="Full name" value={student?.fullName} />
                      <InfoRow label="Email" value={student?.email} />
                      <InfoRow
                        label="Phone"
                        value={formatPhoneNumber(student?.cellPhoneNumber)}
                      />
                      <InfoRow label="Account ID" value={student?.accountId} />
                      <InfoRow
                        label="Date of birth"
                        value={
                          student?.dateOfBirth
                            ? new Date(student.dateOfBirth).toLocaleDateString()
                            : "—"
                        }
                      />
                      <InfoRow
                        label="Account created"
                        value={formatDate(student?.createdAt)}
                      />
                      <InfoRow
                        label="Last login"
                        value={formatDate(student?.lastLogin)}
                      />
                      <InfoRow
                        label="Age"
                        value={(() => {
                          const age = ageFromDateOfBirth(student?.dateOfBirth);
                          return age == null ? "—" : String(age);
                        })()}
                      />
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-3">
                      <div className="space-y-1">
                        <CardTitle className="text-base">Program</CardTitle>
                        {profileProgram.totalEnrollments > 1 && (
                          <CardDescription>
                            Primary enrollment ({profileProgram.totalEnrollments}{" "}
                            total — see Enrollments tab)
                          </CardDescription>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <InfoRow
                        label="Program enrolled"
                        value={profileProgram.programEnrolled}
                      />
                      <InfoRow
                        label="Delivery mode"
                        value={profileProgram.deliveryMode}
                      />
                      <InfoRow
                        label="Registration Date / Enrollment date (EST)"
                        value={profileProgram.enrollmentDateEt}
                      />
                      <InfoRow
                        label="Program service start date"
                        value={profileProgram.programStart}
                      />
                      <InfoRow
                        label="Batch / schedule"
                        value={profileProgram.batchSchedule}
                      />
                      <InfoRow
                        label="Payment plan"
                        value={profileProgram.paymentPlan}
                      />
                      <InfoRow
                        label="Tuition (order)"
                        value={formatCurrency(profileProgram.tuitionAmount)}
                      />
                      <InfoRow
                        label="Enrollment status"
                        value={
                          profileProgram.enrollmentStatus ? (
                            <StatusBadge
                              tone={tonedPaymentStatus(
                                profileProgram.enrollmentStatus
                              )}
                            >
                              {profileProgram.enrollmentStatus}
                            </StatusBadge>
                          ) : (
                            "—"
                          )
                        }
                      />
                      <InfoRow
                        label="Program status"
                        value={
                          profileProgram.programStatus ? (
                            <StatusBadge
                              tone={
                                (profileProgram.programStatus || "").toUpperCase() ===
                                "PAUSED"
                                  ? "warning"
                                  : (profileProgram.programStatus || "").toUpperCase() ===
                                    "CANCELLED"
                                  ? "danger"
                                  : "success"
                              }
                            >
                              {(profileProgram.programStatus || "").toUpperCase() ===
                              "CANCELLED"
                                ? "Dropped"
                                : (profileProgram.programStatus || "").toUpperCase() ===
                                  "PAUSED"
                                ? "Paused"
                                : profileProgram.programStatus}
                            </StatusBadge>
                          ) : (
                            "—"
                          )
                        }
                      />
                      <InfoRow
                        label="Graduation"
                        value={
                          profileProgram.graduation
                            ? [
                                profileProgram.graduation.month,
                                profileProgram.graduation.year,
                              ]
                                .filter(Boolean)
                                .join(" ") || "—"
                            : "—"
                        }
                      />
                    </CardContent>
                  </Card>

                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                    <SummaryStat
                      label="Enrollments"
                      value={enrollments.length.toString()}
                    />
                    <SummaryStat
                      label="Active subs"
                      value={
                        paymentSummary?.activeSubscriptions?.toString() ?? "—"
                      }
                    />
                    <SummaryStat
                      label="Documents pending"
                      value={
                        documentsSummary?.pendingReview?.toString() ?? "—"
                      }
                      tone={
                        documentsSummary && documentsSummary.pendingReview > 0
                          ? "warning"
                          : "default"
                      }
                    />
                    <SummaryStat
                      label="Compliance"
                      value={
                        complianceSummary?.hasActiveSignature
                          ? "Signed"
                          : "Pending"
                      }
                      tone={
                        complianceSummary?.hasActiveSignature
                          ? "success"
                          : "warning"
                      }
                    />
                  </div>
                </TabsContent>

                {/* ─── Enrollments ─────────────────────────────────────── */}
                <TabsContent value="enrollments" className="space-y-4">
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base">
                        Course enrollments
                      </CardTitle>
                      <CardDescription>
                        Programs this student is enrolled in
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      {enrollments.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-10 text-center text-muted-foreground">
                          <ShoppingCart className="mb-2 h-8 w-8 opacity-40" />
                          <p className="text-sm">No enrollments yet</p>
                        </div>
                      ) : (
                        <div className="overflow-hidden rounded-md border">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Program</TableHead>
                                <TableHead>Delivery</TableHead>
                                <TableHead>Start</TableHead>
                                <TableHead>Enrolled (EST)</TableHead>
                                <TableHead>Amount</TableHead>
                                <TableHead>Order status</TableHead>
                                <TableHead>Program status</TableHead>
                                <TableHead>Payment auth</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {enrollments.map((e) => (
                                <TableRow key={e._id}>
                                  <TableCell className="font-medium">
                                    {e.programName || e.program || "—"}
                                  </TableCell>
                                  <TableCell className="text-muted-foreground">
                                    {e.deliveryMode || "—"}
                                  </TableCell>
                                  <TableCell className="text-muted-foreground whitespace-nowrap">
                                    {formatEnrollmentStartDate(
                                      e.startDate,
                                      e.startMonth,
                                    )}
                                  </TableCell>
                                  <TableCell className="whitespace-nowrap text-muted-foreground">
                                    {formatDate(e.enrollmentDate ?? e.createdAt)}
                                  </TableCell>
                                  <TableCell>
                                    {formatCurrency(e.paymentAmount)}
                                  </TableCell>
                                  <TableCell>
                                    {e.orderStatus ? (
                                      <StatusBadge
                                        tone={tonedPaymentStatus(e.orderStatus)}
                                      >
                                        {e.orderStatus}
                                      </StatusBadge>
                                    ) : (
                                      <span className="text-muted-foreground">—</span>
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    <StatusBadge
                                      tone={
                                        (e.programStatus || "ACTIVE").toUpperCase() ===
                                        "PAUSED"
                                          ? "warning"
                                          : (e.programStatus || "ACTIVE").toUpperCase() ===
                                            "CANCELLED"
                                          ? "danger"
                                          : "success"
                                      }
                                    >
                                      {(e.programStatus || "ACTIVE").toUpperCase() ===
                                      "CANCELLED"
                                        ? "Dropped"
                                        : (e.programStatus || "ACTIVE").toUpperCase() ===
                                          "PAUSED"
                                        ? "Paused"
                                        : e.programStatus || "ACTIVE"}
                                    </StatusBadge>
                                  </TableCell>
                                  <TableCell className="min-w-[140px]">
                                    {paymentAuthStatusBadge(
                                      e.paymentAuthorization?.status ?? "not_required"
                                    )}
                                    {e.paymentAuthorization
                                      ? paymentAuthEnrollmentDetails(e.paymentAuthorization)
                                      : null}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* ─── Payments ────────────────────────────────────────── */}
                <TabsContent value="payments" className="space-y-4">
                  {/* Summary cards (from overview, no extra request) */}
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
                    <SummaryStat
                      label="Total payment"
                      value={formatCurrency(
                        paymentSummary?.totalProgramAmount ??
                          (paymentSummary?.totalPaid != null &&
                          paymentSummary?.totalOutstanding != null
                            ? paymentSummary.totalPaid +
                              paymentSummary.totalOutstanding
                            : null)
                      )}
                      tone="info"
                    />
                    <SummaryStat
                      label="Total paid"
                      value={formatCurrency(paymentSummary?.totalPaid ?? null)}
                      tone="success"
                    />
                    <SummaryStat
                      label="Outstanding"
                      value={formatCurrency(
                        paymentSummary?.totalOutstanding ?? null
                      )}
                      tone={
                        paymentSummary && paymentSummary.totalOutstanding > 0
                          ? "danger"
                          : "default"
                      }
                    />
                    <SummaryStat
                      label="Active subs"
                      value={
                        paymentSummary?.activeSubscriptions?.toString() ?? "—"
                      }
                      tone="info"
                    />
                    <SummaryStat
                      label="Success rate"
                      value={
                        paymentSummary?.successRate !== null &&
                        paymentSummary?.successRate !== undefined
                          ? `${paymentSummary.successRate}%`
                          : "—"
                      }
                      tone={
                        paymentSummary?.successRate === 100
                          ? "success"
                          : paymentSummary?.successRate != null &&
                              paymentSummary.successRate > 0
                            ? "warning"
                            : "default"
                      }
                    />
                  </div>

                  {/* Toolbar */}
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex flex-wrap items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => void loadPayments(true)}
                      disabled={paymentsLoading}
                    >
                      {paymentsLoading ? (
                        <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <RefreshCw className="mr-2 h-3.5 w-3.5" />
                      )}
                      Refresh
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const q = student?.email || userId || "";
                        navigate(
                          `/payments?tab=student&search=${encodeURIComponent(q)}`
                        );
                      }}
                    >
                      <ExternalLink className="mr-2 h-3.5 w-3.5" />
                      Payment Reconciliation
                    </Button>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm">
                          <Download className="mr-2 h-3.5 w-3.5" />
                          Export (CSV)
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={() => handleExportPayments("payments")}
                        >
                          Payment history
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => handleExportPayments("orders")}
                        >
                          Orders
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => handleExportPayments("upcoming")}
                        >
                          Upcoming payments
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>

                  {/* Payment schedule (history + upcoming) */}
                  <Card>
                    <CardHeader className="pb-3">
                      <div className="space-y-1">
                        <CardTitle className="text-base">Payment schedule</CardTitle>
                        <CardDescription>
                          Completed, declined, and scheduled installments in one timeline.
                        </CardDescription>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {paymentsLoading && payments === null && upcoming === null ? (
                        <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Loading payments…
                        </div>
                      ) : !hasPaymentSchedule ? (
                        <p className="py-6 text-center text-sm text-muted-foreground">
                          No payments or scheduled installments
                        </p>
                      ) : (
                        <>
                          {upcomingSummary?.nextPayment && (
                            <div className="grid gap-3 sm:grid-cols-3">
                              <SummaryStat
                                label="Next payment (EST)"
                                value={
                                  upcomingSummary.nextPayment.formattedDate ||
                                  formatDate(
                                    upcomingPaymentDate(
                                      upcomingSummary.nextPayment
                                    )
                                  )
                                }
                              />
                              <SummaryStat
                                label="Next amount"
                                value={
                                  upcomingSummary.nextPayment.isOverdue &&
                                  (upcomingSummary.nextPayment.amountWithLateFee ??
                                    0) >
                                    upcomingSummary.nextPayment.amount
                                    ? `${formatCurrency(upcomingSummary.nextPayment.amount)} + late fee`
                                    : formatCurrency(
                                        upcomingSummary.nextPayment.amount
                                      )
                                }
                              />
                              <SummaryStat
                                label="Remaining installments"
                                value={upcomingSummary.total ?? upcoming?.length ?? 0}
                              />
                            </div>
                          )}
                          {(() => {
                            const overdueWithFee =
                              upcoming?.filter(
                                (p) => p.isOverdue && p.lateFeeApplied
                              ).length ?? 0;
                            const overdueMissingFee =
                              upcoming?.filter(
                                (p) =>
                                  p.isOverdue &&
                                  !p.lateFeeApplied &&
                                  (p.lateFeeAmount ?? 0) > 0 &&
                                  !p.inLateFeeGracePeriod &&
                                  p.lateFeeStatus !== "IN_GRACE"
                              ).length ?? 0;
                            const overdueInGrace =
                              upcoming?.filter(
                                (p) =>
                                  p.isOverdue &&
                                  (p.inLateFeeGracePeriod ||
                                    p.lateFeeStatus === "IN_GRACE")
                              ).length ?? 0;
                            if (
                              overdueWithFee === 0 &&
                              overdueMissingFee === 0 &&
                              overdueInGrace === 0
                            ) {
                              return null;
                            }
                            return (
                              <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm">
                                {overdueWithFee > 0 && (
                                  <p className="text-destructive">
                                    {overdueWithFee} overdue installment
                                    {overdueWithFee === 1 ? "" : "s"} with $25 late
                                    fee applied.
                                  </p>
                                )}
                                {overdueInGrace > 0 && (
                                  <p className="text-muted-foreground">
                                    {overdueInGrace} overdue installment
                                    {overdueInGrace === 1 ? "" : "s"} within 3-day
                                    grace (late fee not applied yet).
                                  </p>
                                )}
                                {overdueMissingFee > 0 && (
                                  <p className="text-muted-foreground">
                                    {overdueMissingFee} overdue installment
                                    {overdueMissingFee === 1 ? "" : "s"} past grace
                                    still missing late fee application.
                                  </p>
                                )}
                              </div>
                            );
                          })()}
                          <div className="overflow-hidden rounded-md border">
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>#</TableHead>
                                  <TableHead>Payment date (EST)</TableHead>
                                  <TableHead>Chargable date (EST)</TableHead>
                                  <TableHead>Type</TableHead>
                                  <TableHead>Amount</TableHead>
                                  <TableHead>Status</TableHead>
                                  <TableHead className="w-[72px] text-right">
                                    Action
                                  </TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {unifiedPaymentRows.map((row) => {
                                  const canDownload =
                                    row.kind === "history" &&
                                    canDownloadChargebackEvidence(row.history);
                                  const downloadId =
                                    row.kind === "history" ? row.history._id : null;
                                  const paymentAuthStatus = downloadId
                                    ? paymentAuthByPaymentId.get(downloadId)
                                    : undefined;
                                  const authDownloadDisabledReason =
                                    canDownload && downloadId
                                      ? paymentAuthDownloadDisabledReason(
                                          paymentAuthStatus
                                        )
                                      : null;
                                  const isOverdueUpcoming =
                                    row.kind === "upcoming" &&
                                    (row.upcoming.isOverdue ||
                                      row.statusLabel === "Overdue");
                                  const canMarkPaid =
                                    row.kind === "upcoming" &&
                                    Boolean(row.upcoming.isNextPayment) &&
                                    Boolean(row.upcoming.subscriptionId) &&
                                    (row.upcoming.paymentNumber ?? 0) > 0;
                                  const historyNumber =
                                    row.kind === "history"
                                      ? historyInstallmentNumber(row.history)
                                      : null;
                                  const canMarkUnpaid =
                                    row.kind === "history" &&
                                    latestPaidIds.has(row.history._id) &&
                                    Boolean(row.history.subscriptionId) &&
                                    historyNumber != null &&
                                    historyNumber > 0;
                                  const manualSourceLabel =
                                    row.kind === "history" &&
                                    !isReversedInstallment(row.history)
                                      ? manualPaidLabel(row.history.metadata?.manualReason)
                                      : null;
                                  const canWaiveLateFee =
                                    canWaiveScheduleLateFee(row) &&
                                    row.kind === "upcoming" &&
                                    Boolean(row.upcoming.subscriptionId);

                                  return (
                                    <TableRow
                                      key={row.id}
                                      className={
                                        row.highlight ? "bg-primary/5" : undefined
                                      }
                                    >
                                      <TableCell className="tabular-nums text-muted-foreground">
                                        {row.installmentLabel ?? "—"}
                                      </TableCell>
                                      <TableCell className="whitespace-nowrap">
                                        <span
                                          className={
                                            isOverdueUpcoming
                                              ? "font-medium text-destructive"
                                              : row.kind === "history"
                                                ? "text-muted-foreground"
                                                : undefined
                                          }
                                        >
                                          {formatDate(row.dateIso)}
                                        </span>
                                        {row.kind === "history" && (
                                          <p className="text-xs text-muted-foreground">
                                            Processed
                                          </p>
                                        )}
                                        {row.kind === "upcoming" &&
                                          row.upcoming.daysUntilPayment != null &&
                                          row.upcoming.daysUntilPayment < 0 && (
                                            <p className="text-xs text-destructive">
                                              Past due
                                            </p>
                                          )}
                                      </TableCell>
                                      <TableCell className="whitespace-nowrap">
                                        {row.chargableDueDateIso ? (
                                          <>
                                            <span className="font-medium">
                                              {formatDate(row.chargableDueDateIso)}
                                            </span>
                                            {row.kind === "history" &&
                                              row.chargableDueDateIso !== row.dateIso && (
                                                <p className="text-xs text-muted-foreground">
                                                  Installment due
                                                </p>
                                              )}
                                          </>
                                        ) : (
                                          <span className="text-muted-foreground">—</span>
                                        )}
                                      </TableCell>
                                      <TableCell className="text-muted-foreground text-xs uppercase tracking-wide">
                                        {row.detailLabel}
                                      </TableCell>
                                      <TableCell className="font-medium tabular-nums">
                                        <div>{formatCurrency(row.amount)}</div>
                                        {isOverdueUpcoming &&
                                          !row.inLateFeeGracePeriod &&
                                          row.lateFeeStatus !== "IN_GRACE" &&
                                          (row.lateFeeAmount ?? 0) > 0 && (
                                            <div className="mt-1 space-y-0.5">
                                              <p
                                                className={
                                                  row.lateFeeApplied
                                                    ? "text-xs font-medium text-destructive"
                                                    : "text-xs font-medium text-muted-foreground"
                                                }
                                              >
                                                {row.lateFeeApplied
                                                  ? row.lateFeeStatus === "PENDING"
                                                    ? `+ ${formatCurrency(row.lateFeeAmount)} late fee (pending)`
                                                    : `+ ${formatCurrency(row.lateFeeAmount)} late fee applied`
                                                  : `+ ${formatCurrency(row.lateFeeAmount)} late fee due`}
                                              </p>
                                              <p className="text-xs text-muted-foreground">
                                                Total due{" "}
                                                {formatCurrency(
                                                  row.amountWithLateFee ??
                                                    row.amount + (row.lateFeeAmount ?? 0)
                                                )}
                                              </p>
                                            </div>
                                          )}
                                      </TableCell>
                                      <TableCell>
                                        <div className="flex flex-col items-start gap-1">
                                          <StatusBadge tone={row.statusTone}>
                                            {row.statusLabel}
                                          </StatusBadge>
                                          {manualSourceLabel ? (
                                            <StatusBadge tone="muted">
                                              {manualSourceLabel}
                                            </StatusBadge>
                                          ) : null}
                                          {isOverdueUpcoming &&
                                            (row.inLateFeeGracePeriod ||
                                              row.lateFeeStatus === "IN_GRACE") && (
                                              <StatusBadge tone="muted">
                                                Grace period
                                                {(row.lateFeeGraceDaysRemaining ?? 0) > 0
                                                  ? ` (${row.lateFeeGraceDaysRemaining}d left)`
                                                  : ""}
                                              </StatusBadge>
                                            )}
                                          {isOverdueUpcoming && row.lateFeeApplied && (
                                            <StatusBadge tone="warning">
                                              Late fee{" "}
                                              {row.lateFeeStatus === "PENDING"
                                                ? "pending"
                                                : "applied"}
                                            </StatusBadge>
                                          )}
                                          {isOverdueUpcoming &&
                                            !row.lateFeeApplied &&
                                            !row.inLateFeeGracePeriod &&
                                            row.lateFeeStatus !== "IN_GRACE" &&
                                            (row.lateFeeAmount ?? 0) > 0 && (
                                              <StatusBadge tone="muted">
                                                Late fee not applied
                                              </StatusBadge>
                                            )}
                                        </div>
                                      </TableCell>
                                      <TableCell className="text-right">
                                        <DropdownMenu>
                                          <DropdownMenuTrigger asChild>
                                            <Button
                                              variant="ghost"
                                              size="sm"
                                              className="h-8 w-8 p-0"
                                              aria-label="Payment actions"
                                            >
                                              <MoreVertical className="h-4 w-4" />
                                            </Button>
                                          </DropdownMenuTrigger>
                                          <DropdownMenuContent align="end">
                                            {canDownload && downloadId ? (
                                              <>
                                                <DropdownMenuItem
                                                  disabled={
                                                    evidenceDownloadingId ===
                                                    downloadId
                                                  }
                                                  onClick={() =>
                                                    void handleDownloadChargebackEvidence(
                                                      downloadId
                                                    )
                                                  }
                                                >
                                                  {evidenceDownloadingId ===
                                                  downloadId ? (
                                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                                  ) : (
                                                    <Download className="mr-2 h-4 w-4" />
                                                  )}
                                                  Download case
                                                </DropdownMenuItem>
                                                <PaymentAuthMenuItem
                                                  disabled={Boolean(
                                                    authDownloadDisabledReason
                                                  )}
                                                  disabledReason={
                                                    authDownloadDisabledReason
                                                  }
                                                  busy={
                                                    authDownloadingId === downloadId
                                                  }
                                                  onSelect={() =>
                                                    void handleDownloadPaymentAuthorization(
                                                      downloadId
                                                    )
                                                  }
                                                >
                                                  {authDownloadingId === downloadId ? (
                                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                                  ) : (
                                                    <Download className="mr-2 h-4 w-4" />
                                                  )}
                                                  Download authorization
                                                </PaymentAuthMenuItem>
                                              </>
                                            ) : null}
                                            {canWaiveLateFee && row.kind === "upcoming" ? (
                                              <DropdownMenuItem
                                                onClick={() => {
                                                  setWaiveReason("");
                                                  setWaiveFeeTarget({
                                                    mode: "installment",
                                                    subscriptionId:
                                                      row.upcoming.subscriptionId!,
                                                    paymentNumber:
                                                      row.upcoming.paymentNumber,
                                                    dueDate:
                                                      upcomingPaymentDate(
                                                        row.upcoming
                                                      )?.slice(0, 10) ||
                                                      undefined,
                                                    amount:
                                                      row.lateFeeAmount ??
                                                      row.upcoming.lateFeeAmount ??
                                                      25,
                                                    lateFeeRecordId:
                                                      row.upcoming.lateFeeRecordId,
                                                  });
                                                }}
                                              >
                                                Waive late fee
                                              </DropdownMenuItem>
                                            ) : null}
                                            {canMarkPaid && row.kind === "upcoming" ? (
                                              <DropdownMenuItem
                                                onClick={() => {
                                                  setManualReason("");
                                                  setManualNote("");
                                                  setManualTarget({
                                                    action: "MARK_PAID",
                                                    subscriptionId:
                                                      row.upcoming.subscriptionId!,
                                                    paymentNumber:
                                                      row.upcoming.paymentNumber!,
                                                    amount: row.amount,
                                                    installmentLabel: row.installmentLabel,
                                                  });
                                                }}
                                              >
                                                Mark as paid
                                              </DropdownMenuItem>
                                            ) : null}
                                            {canMarkUnpaid && row.kind === "history" ? (
                                              <DropdownMenuItem
                                                onClick={() => {
                                                  setManualReason("");
                                                  setManualNote("");
                                                  setManualTarget({
                                                    action: "MARK_UNPAID",
                                                    subscriptionId:
                                                      row.history.subscriptionId!,
                                                    paymentNumber: historyNumber!,
                                                    paymentId: row.history._id,
                                                    amount: row.amount,
                                                    installmentLabel: row.installmentLabel,
                                                  });
                                                }}
                                              >
                                                Mark as unpaid
                                              </DropdownMenuItem>
                                            ) : null}
                                            {!(
                                              (canDownload && downloadId) ||
                                              canWaiveLateFee ||
                                              canMarkPaid ||
                                              canMarkUnpaid
                                            ) ? (
                                              <DropdownMenuItem disabled>
                                                No actions available
                                              </DropdownMenuItem>
                                            ) : null}
                                          </DropdownMenuContent>
                                        </DropdownMenu>
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </div>
                        </>
                      )}
                    </CardContent>
                  </Card>

                  {/* Delay fees */}
                  <Card>
                    <CardHeader className="pb-3 flex flex-row items-center justify-between space-y-0">
                      <div>
                        <CardTitle className="text-base">Delay fees</CardTitle>
                        <CardDescription>
                          Late payment fees applied to subscriptions. Waive unpaid
                          fees when a genuine hardship request is approved.
                        </CardDescription>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => void loadDelayFees(true)}
                        disabled={delayFeesLoading}
                      >
                        {delayFeesLoading ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <RefreshCw className="h-3.5 w-3.5" />
                        )}
                      </Button>
                    </CardHeader>
                    <CardContent>
                      {!delayFees ? (
                        <p className="py-3 text-sm text-muted-foreground">
                          {delayFeesLoading ? "Loading…" : "No delay fee data"}
                        </p>
                      ) : (
                        <>
                          <div className="grid grid-cols-3 gap-3">
                            <SummaryStat
                              label="Total"
                              value={formatCurrency(delayFees.totalDelayFees ?? null)}
                            />
                            <SummaryStat
                              label="Applied"
                              value={(delayFees.delayFeesApplied ?? 0).toString()}
                            />
                            <SummaryStat
                              label="Pending"
                              value={(delayFees.pendingDelayFees ?? 0).toString()}
                              tone={
                                (delayFees.pendingDelayFees ?? 0) > 0
                                  ? "warning"
                                  : "default"
                              }
                            />
                          </div>
                          {delayFees.delayFeeRecords && delayFees.delayFeeRecords.length > 0 && (
                            <div className="mt-4 overflow-hidden rounded-md border">
                              <Table>
                                <TableHeader>
                                  <TableRow>
                                    <TableHead>Date</TableHead>
                                    <TableHead>Amount</TableHead>
                                    <TableHead>Status</TableHead>
                                    <TableHead>Reason</TableHead>
                                    <TableHead className="w-[100px] text-right">
                                      Action
                                    </TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {delayFees.delayFeeRecords.map((f, idx) => (
                                    <TableRow key={f._id ?? idx}>
                                      <TableCell className="whitespace-nowrap">
                                        {formatDate(f.timestamp)}
                                      </TableCell>
                                      <TableCell>{formatCurrency(f.amount)}</TableCell>
                                      <TableCell>
                                        <StatusBadge tone={tonedPaymentStatus(f.status)}>
                                          {f.status}
                                        </StatusBadge>
                                      </TableCell>
                                      <TableCell className="max-w-[220px] min-w-0 whitespace-normal break-words text-muted-foreground">
                                        {f.status?.toUpperCase() === "WAIVED"
                                          ? f.metadata?.waiverReason ||
                                            f.metadata?.reason ||
                                            "—"
                                          : f.metadata?.reason || "—"}
                                      </TableCell>
                                      <TableCell className="text-right">
                                        {f._id && canWaiveDelayFee(f.status) ? (
                                          <Button
                                            variant="outline"
                                            size="sm"
                                            className="h-8"
                                            onClick={() => {
                                              setWaiveReason("");
                                              setWaiveFeeTarget({
                                                mode: "record",
                                                _id: f._id!,
                                                amount: f.amount,
                                              });
                                            }}
                                          >
                                            Waive
                                          </Button>
                                        ) : (
                                          <span className="text-xs text-muted-foreground">
                                            —
                                          </span>
                                        )}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </div>
                          )}
                        </>
                      )}
                    </CardContent>
                  </Card>

                  {/* Checkout activity */}
                  <Card>
                    <CardHeader className="pb-3 flex flex-row items-center justify-between space-y-0">
                      <div>
                        <CardTitle className="text-base">
                          Checkout activity
                        </CardTitle>
                        <CardDescription>
                          Recent checkout events for this student
                        </CardDescription>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => void loadCheckoutEvents(1)}
                        disabled={checkoutLoading}
                      >
                        {checkoutLoading ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Clock className="h-3.5 w-3.5" />
                        )}
                      </Button>
                    </CardHeader>
                    <CardContent>
                      {!checkoutEvents ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => void loadCheckoutEvents(1)}
                          disabled={checkoutLoading}
                        >
                          {checkoutLoading ? "Loading…" : "Load checkout history"}
                        </Button>
                      ) : checkoutEvents.length === 0 ? (
                        <p className="py-3 text-sm text-muted-foreground">
                          No checkout events recorded
                        </p>
                      ) : (
                        <>
                          <div className="overflow-hidden rounded-md border">
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>Time</TableHead>
                                  <TableHead>Event</TableHead>
                                  <TableHead>Status</TableHead>
                                  <TableHead>Amount</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {checkoutEvents.map((event) => (
                                  <TableRow key={event._id}>
                                    <TableCell className="whitespace-nowrap text-muted-foreground">
                                      {formatDate(event.timestamp)}
                                    </TableCell>
                                    <TableCell className="text-muted-foreground">
                                      {event.eventType}
                                    </TableCell>
                                    <TableCell>
                                      <StatusBadge tone={tonedPaymentStatus(event.status)}>
                                        {event.status}
                                      </StatusBadge>
                                    </TableCell>
                                    <TableCell>
                                      {event.amount ? formatCurrency(event.amount) : "—"}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                          {checkoutTotal > 10 && (
                            <div className="mt-3 flex items-center justify-between text-sm text-muted-foreground">
                              <span>
                                Showing {checkoutEvents.length} of {checkoutTotal}
                              </span>
                              <div className="flex gap-1">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => void loadCheckoutEvents(checkoutPage - 1)}
                                  disabled={checkoutPage <= 1 || checkoutLoading}
                                >
                                  Prev
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => void loadCheckoutEvents(checkoutPage + 1)}
                                  disabled={checkoutEvents.length < 10 || checkoutLoading}
                                >
                                  Next
                                </Button>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* ─── Compliance (Terms + Signatures merged) ──────────── */}
                <TabsContent value="compliance" className="space-y-4">
                  <Card>
                    <CardHeader className="pb-3 flex flex-row items-center justify-between space-y-0">
                      <div>
                        <CardTitle className="text-base">
                          Terms & signatures
                        </CardTitle>
                        <CardDescription>
                          Agreement records and digital signature
                        </CardDescription>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => void loadCompliance(true)}
                        disabled={complianceLoading}
                      >
                        {complianceLoading ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <RefreshCw className="h-3.5 w-3.5" />
                        )}
                      </Button>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {/* High-level status from overview */}
                      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                        <SummaryStat
                          label="Signed"
                          value={complianceSummary?.hasActiveSignature ? "Yes" : "No"}
                          tone={complianceSummary?.hasActiveSignature ? "success" : "warning"}
                        />
                        <SummaryStat
                          label="Terms version"
                          value={complianceSummary?.termsVersion ?? "—"}
                        />
                        <SummaryStat
                          label="Agreements"
                          value={(complianceSummary?.totalAgreements ?? 0).toString()}
                        />
                      </div>

                      {/* Consent preferences */}
                      {complianceSummary && (
                        <div className="rounded-md border bg-muted/30 p-3">
                          <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                            Consent preferences
                          </p>
                          <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-3">
                            <ConsentRow
                              label="Data processing"
                              value={complianceSummary.dataProcessingConsent}
                            />
                            <ConsentRow
                              label="Marketing"
                              value={complianceSummary.marketingConsent}
                            />
                            <ConsentRow
                              label="Third party"
                              value={complianceSummary.thirdPartySharingConsent}
                            />
                          </div>
                        </div>
                      )}

                      {/* Detailed agreements list (lazy) */}
                      {complianceLoading && !compliance ? (
                        <div className="flex items-center justify-center py-6 text-sm text-muted-foreground">
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Loading agreements…
                        </div>
                      ) : compliance?.agreements && compliance.agreements.length > 0 ? (
                        <div className="space-y-2">
                          {compliance.agreements.map((agreement, idx) => (
                            <div
                              key={agreement.signatureId || idx}
                              className="rounded-md border bg-background p-3"
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                  <p className="text-sm font-medium text-foreground">
                                    {agreement.program || "Account-level signature"}
                                  </p>
                                  <p className="text-xs text-muted-foreground">
                                    {formatDate(agreement.agreedAt)} · v{agreement.termsVersion || "1.0"}
                                  </p>
                                </div>
                                <div className="flex shrink-0 items-center gap-2">
                                  <StatusBadge
                                    tone={agreement.hasSignature ? "success" : "muted"}
                                  >
                                    {agreement.hasSignature ? "Signed" : "Unsigned"}
                                  </StatusBadge>
                                  {agreement.signatureId && agreement.hasSignature && (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() =>
                                        agreement.signatureId &&
                                        void handleViewSignature(agreement.signatureId)
                                      }
                                    >
                                      <Eye className="h-3.5 w-3.5" />
                                    </Button>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        compliance && (
                          <p className="py-3 text-center text-sm text-muted-foreground">
                            No agreement records found
                          </p>
                        )
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* ─── Documents ───────────────────────────────────────── */}
                <TabsContent value="documents" className="space-y-4">
                  <DocumentList
                    documents={documents ?? []}
                    summary={
                      documentSummary ?? {
                        total: 0,
                        uploaded: 0,
                        required: 0,
                        optional: 0,
                        pending: 0,
                        approved: 0,
                        rejected: 0,
                        missingRequired: 0,
                      }
                    }
                    missingRequired={missingRequiredDocs}
                    onApprove={handleDocApprove}
                    onReject={handleDocReject}
                    onView={handleDocView}
                    onRefresh={() => loadDocuments(true)}
                    isUpdating={docsUpdating.size > 0}
                    isLoading={documentsLoading}
                    viewingDocumentId={docViewingId}
                  />
                </TabsContent>
              </Tabs>
            )}
          </div>
        </SheetContent>
      </Sheet>

      <DocumentViewer
        document={viewerDoc}
        open={docViewerOpen}
        onOpenChange={(isOpen) => {
          setDocViewerOpen(isOpen);
          if (!isOpen) {
            setViewerDoc(null);
            setViewerUrl(undefined);
          }
        }}
        fileUrl={viewerUrl}
      />

      {/* Signature viewer modal */}
      <AlertDialog
        open={signatureModal.open}
        onOpenChange={(o) =>
          setSignatureModal((s) => ({ ...s, open: o, loading: false }))
        }
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Digital signature</AlertDialogTitle>
            <AlertDialogDescription>
              {signatureModal.timestamp
                ? `Signed ${formatDate(signatureModal.timestamp)}`
                : "Signature image"}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex min-h-[160px] items-center justify-center rounded-md border bg-muted/30">
            {signatureModal.loading ? (
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            ) : signatureModal.url ? (
              <img
                src={signatureModal.url}
                alt="User signature"
                className="max-h-72 max-w-full object-contain"
              />
            ) : (
              <p className="text-sm text-muted-foreground">
                Signature image unavailable
              </p>
            )}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Close</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete confirmation */}
      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this user?</AlertDialogTitle>
            <AlertDialogDescription>
              {`This soft-deletes the account and all associated data. ${
                headerName || "The user"
              } will no longer be able to sign in.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setConfirmDelete(false);
                onDelete();
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete user
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog
        open={lifecycleAction !== null}
        onOpenChange={(open) => {
          if (!open && !lifecycleSubmitting) {
            setLifecycleAction(null);
            setLifecycleEnrollmentId(null);
            setLifecycleReason("");
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {lifecycleAction === "pause"
                ? "Pause enrollment"
                : lifecycleAction === "resume"
                ? "Resume enrollment"
                : "Drop enrollment"}
            </DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-2 text-sm text-muted-foreground">
                <p>
                  Only the selected program enrollment is affected. Other
                  programs for this student stay unchanged.
                </p>
                {lifecycleCandidates.length <= 1 ? (
                  <p>
                    Program:{" "}
                    <span className="font-medium text-foreground">
                      {lifecycleTargetEnrollment?.programName ||
                        lifecycleTargetEnrollment?.program ||
                        "—"}
                    </span>
                  </p>
                ) : null}
                {lifecycleAction === "pause" && (
                  <p>
                    This pauses future Square installments and revokes course /
                    iSpring access. The student can still sign in. You can resume
                    later; remaining payments restart from the resume day.
                  </p>
                )}
                {lifecycleAction === "resume" && (
                  <p>
                    This resumes billing from today (remaining installments keep
                    the same amounts) and restores course / iSpring access.
                  </p>
                )}
                {lifecycleAction === "drop" && (
                  <p>
                    This cancels future Square payments only (amounts already paid
                    are kept), drops the student from the program, and deactivates
                    course keys / iSpring. This cannot be undone from Resume.
                  </p>
                )}
              </div>
            </DialogDescription>
          </DialogHeader>
          {lifecycleCandidates.length > 1 && (
            <div className="space-y-2 py-2">
              <Label>Select program</Label>
              <RadioGroup
                value={lifecycleTargetEnrollment?._id || ""}
                onValueChange={setLifecycleEnrollmentId}
                disabled={lifecycleSubmitting}
                className="gap-2"
              >
                {lifecycleCandidates.map((e) => (
                  <div
                    key={e._id}
                    className="flex items-center space-x-2 rounded-md border border-border px-3 py-2"
                  >
                    <RadioGroupItem value={e._id} id={`lifecycle-enroll-${e._id}`} />
                    <Label
                      htmlFor={`lifecycle-enroll-${e._id}`}
                      className="flex-1 cursor-pointer font-normal"
                    >
                      <span className="font-medium text-foreground">
                        {e.programName || e.program || "Program"}
                      </span>
                      <span className="ml-2 text-xs text-muted-foreground">
                        {programStatusOf(e)}
                      </span>
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </div>
          )}
          {(lifecycleAction === "pause" || lifecycleAction === "drop") && (
            <div className="space-y-2 py-2">
              <Label htmlFor="lifecycle-reason">Reason (optional)</Label>
              <Textarea
                id="lifecycle-reason"
                value={lifecycleReason}
                onChange={(e) => setLifecycleReason(e.target.value)}
                placeholder="e.g. Student requested pause until next month"
                rows={3}
                disabled={lifecycleSubmitting}
              />
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setLifecycleAction(null);
                setLifecycleEnrollmentId(null);
                setLifecycleReason("");
              }}
              disabled={lifecycleSubmitting}
            >
              Cancel
            </Button>
            <Button
              variant={lifecycleAction === "drop" ? "destructive" : "default"}
              onClick={() => void submitLifecycleAction()}
              disabled={lifecycleSubmitting || !lifecycleTargetEnrollment?._id}
            >
              {lifecycleSubmitting ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : null}
              {lifecycleAction === "pause"
                ? "Pause"
                : lifecycleAction === "resume"
                ? "Resume"
                : "Drop"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(waiveFeeTarget)}
        onOpenChange={(open) => {
          if (!open) {
            setWaiveFeeTarget(null);
            setWaiveReason("");
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Waive delay fee</DialogTitle>
            <DialogDescription>
              Remove the{" "}
              {waiveFeeTarget
                ? formatCurrency(waiveFeeTarget.amount)
                : "$25"}{" "}
              late fee so the student only owes the subscription installment. A
              reason is required for audit records.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="delay-fee-waiver-reason">Waiver reason</Label>
            <Textarea
              id="delay-fee-waiver-reason"
              value={waiveReason}
              onChange={(e) => setWaiveReason(e.target.value)}
              placeholder="e.g. Student had a documented medical emergency and requested a one-time waiver."
              rows={4}
              disabled={waiveSubmitting}
            />
            <p className="text-xs text-muted-foreground">
              Minimum 3 characters. This is stored with the fee record.
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setWaiveFeeTarget(null);
                setWaiveReason("");
              }}
              disabled={waiveSubmitting}
            >
              Cancel
            </Button>
            <Button
              onClick={() => void handleWaiveDelayFee()}
              disabled={waiveSubmitting || waiveReason.trim().length < 3}
            >
              {waiveSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Waiving…
                </>
              ) : (
                "Confirm waive"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(manualTarget)}
        onOpenChange={(open) => {
          if (!open && !manualSubmitting) {
            setManualTarget(null);
            setManualReason("");
            setManualNote("");
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {manualTarget?.action === "MARK_UNPAID"
                ? "Mark installment as unpaid"
                : "Mark installment as paid"}
            </DialogTitle>
            <DialogDescription>
              {manualTarget
                ? `${manualTarget.installmentLabel ? `Installment ${manualTarget.installmentLabel}` : "This installment"} for ${formatCurrency(manualTarget.amount)} will be ${
                    manualTarget.action === "MARK_PAID"
                      ? "recorded as paid. Square will skip this installment so the card is not charged for it."
                      : "marked unpaid and shown as the next amount due. Square will not charge the following installment until this one is collected."
                  }`
                : ""}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Reason</Label>
              <RadioGroup
                value={manualReason}
                onValueChange={setManualReason}
                disabled={manualSubmitting}
              >
                {(manualTarget?.action === "MARK_UNPAID"
                  ? MANUAL_UNPAID_REASON_OPTIONS
                  : MANUAL_PAID_REASON_OPTIONS
                ).map((option) => (
                  <div key={option.value} className="flex items-center gap-2">
                    <RadioGroupItem value={option.value} id={`manual-reason-${option.value}`} />
                    <Label htmlFor={`manual-reason-${option.value}`} className="font-normal">
                      {option.label}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </div>
            <div className="space-y-2">
              <Label htmlFor="manual-installment-note">Note (optional)</Label>
              <Textarea
                id="manual-installment-note"
                value={manualNote}
                onChange={(event) => setManualNote(event.target.value)}
                placeholder="Add a short note for the audit record"
                rows={3}
                maxLength={500}
                disabled={manualSubmitting}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setManualTarget(null);
                setManualReason("");
                setManualNote("");
              }}
              disabled={manualSubmitting}
            >
              Cancel
            </Button>
            <Button
              variant={manualTarget?.action === "MARK_UNPAID" ? "destructive" : "default"}
              onClick={() => void handleManualInstallmentStatus()}
              disabled={manualSubmitting || !manualReason}
            >
              {manualSubmitting ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : null}
              {manualTarget?.action === "MARK_UNPAID"
                ? "Confirm mark as unpaid"
                : "Confirm mark as paid"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function SummaryStat({
  label,
  value,
  tone = "default",
}: {
  label: string;
  value: React.ReactNode;
  tone?: "default" | "success" | "warning" | "danger" | "info";
}) {
  const valueClass =
    tone === "success"
      ? "text-emerald-600 dark:text-emerald-400"
      : tone === "warning"
        ? "text-amber-600 dark:text-amber-400"
        : tone === "danger"
          ? "text-destructive"
          : tone === "info"
            ? "text-sky-600 dark:text-sky-400"
            : "text-foreground";
  return (
    <div className="rounded-md border bg-card p-3">
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <p className={`mt-1 text-lg font-semibold ${valueClass}`}>{value}</p>
    </div>
  );
}

function ConsentRow({
  label,
  value,
}: {
  label: string;
  value: boolean | null | undefined;
}) {
  return (
    <div className="flex items-center gap-2 text-sm">
      <span
        className={`h-2 w-2 rounded-full ${
          value === true
            ? "bg-emerald-500"
            : value === false
            ? "bg-destructive"
            : "bg-muted-foreground/40"
        }`}
        aria-hidden
      />
      <span className="text-muted-foreground">{label}:</span>
      <span className="font-medium text-foreground">
        {value === true ? "Agreed" : value === false ? "Declined" : "—"}
      </span>
    </div>
  );
}
